/* eslint-disable no-undef, no-new, no-unused-vars, camelcase */
const EARTH_RADIUS_MILES = 3959
const EARTH_RADIUS_KM = 6371
const MAP_BOUNDS_PADDING = { top: 20, right: 20, bottom: 20, left: 20 }
const INFO_WINDOW_WIDTH = 320
const WINDOW_OFFSET = 180
const ZOOM_INCREASE = 3
const GOOGLE_MAPS_LOAD_TIMEOUT_MS = 15000

function waitForGoogleMapsBootstrap () {
  if (typeof google !== 'undefined' && google.maps?.importLibrary) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const script = document.querySelector(
      'script[src*="maps.googleapis.com"], script[src*="maps.google.com/maps/api/js"]',
    )
    if (!script) {
      reject(new Error('Google Maps script tag not found'))
      return
    }

    let settled = false
    let poll = null
    let timeout = null

    const cleanup = () => {
      if (poll) clearInterval(poll)
      if (timeout) clearTimeout(timeout)
    }

    const tryResolve = () => {
      if (settled) return
      if (typeof google !== 'undefined' && google.maps?.importLibrary) {
        settled = true
        cleanup()
        resolve()
      }
    }

    const rejectWith = (error) => {
      if (settled) return
      settled = true
      cleanup()
      reject(error)
    }

    poll = setInterval(tryResolve, 50)
    timeout = setTimeout(() => {
      rejectWith(new Error('Google Maps failed to load'))
    }, GOOGLE_MAPS_LOAD_TIMEOUT_MS)

    script.addEventListener('load', tryResolve, { once: true })
    script.addEventListener('error', () => {
      rejectWith(new Error('Google Maps script failed to load'))
    }, { once: true })

    tryResolve()
  })
}

async function importGoogleMapsLibraries () {
  await Promise.all([
    google.maps.importLibrary('maps'),
    google.maps.importLibrary('marker'),
    google.maps.importLibrary('geocoding'),
  ])

  if (typeof google.maps.Map !== 'function' || !google.maps.marker?.AdvancedMarkerElement) {
    throw new Error('Google Maps libraries not fully loaded')
  }

  return google.maps
}

function whenGoogleMapsReady () {
  return waitForGoogleMapsBootstrap().then(() => importGoogleMapsLibraries())
}
class ShowroomBooking extends HTMLElement {
  constructor () {
    super()
    this.map = null
    this.markers = new Map()
    this.showroomMarkers = []
    this.bounds = null
    this.closestShowroomsText = this.querySelector('.showroom-booking__closet-showrooms-text')
    this.showroomIcon = this.dataset.showroomIcon
    this.mapCenterCoordinates = this.dataset.mapCenterCoordinates?.split(',').map(coord => parseFloat(coord.trim())) || [53.36009499748501, -1.344126373082382]
    this.mapId = this.dataset.mapId || 'DEMO_MAP_ID'
    this.initialZoomLevel = parseInt(this.dataset.initialZoomLevel) || 5
    this.searchInputType = this.querySelector('input[name="location"]')?.getAttribute('autocomplete') === 'postal-code' ? 'postcode' : 'address'
    this.isEuStore = this.dataset.isEuStore === 'true'
    this.currentMarketHandle = this.dataset.currentMarketHandle || ''
    this.currentCountryCode = this.dataset.currentCountryCode || ''
    this.showAdditionalInfo = this.dataset.showAdditionalInfo === 'true'
    this.bookLabel = this.dataset.bookLabel || ''
    this.userChangedZoom = false
    this.hasZoomedToMarker = false
    this.resizeTimeout = null
    this.lastWindowWidth = window.innerWidth
    // Store EU store icons
    this.phoneIcon = this.dataset.phoneIcon || ''
    this.addressIcon = this.dataset.addressIcon || ''
    this.emailIcon = this.dataset.emailIcon || ''
    this.websiteIcon = this.dataset.websiteIcon || ''
    if (!this.showroomIcon.startsWith('http')) {
      this.showroomIcon = `https:${this.showroomIcon}`
    }
    this.handleScroll = this.handleScroll.bind(this)
    this.bookingModal = null
    this.setupBookingModal()
    this._googleMapsReady = null
  }

  ensureGoogleMaps () {
    if (!this._googleMapsReady) {
      this._googleMapsReady = whenGoogleMapsReady()
    }
    return this._googleMapsReady
  }

  getBaseMapOptions (overrides = {}) {
    return {
      mapId: this.mapId,
      gestureHandling: 'cooperative',
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      scaleControl: true,
      rotateControl: false,
      disableDefaultUI: false,
      ...overrides,
    }
  }

  setMarkerMap (marker, map) {
    marker.map = map || null
  }

  getMarkerPosition (marker) {
    const position = marker.position
    if (!position) return null
    if (typeof position.lat === 'function') {
      return { lat: position.lat(), lng: position.lng() }
    }
    return position
  }

  isMarkerVisible (marker) {
    return marker.map === this.map
  }

  setupBookingModal () {
    const apiBase = this.dataset.toolkitApiBase || 'https://tentbox.tools'
    const store = this.dataset.toolkitStore || ''
    const locale = this.dataset.locale || document.documentElement.lang || 'en'
    let strings = {}
    try {
      strings = JSON.parse(this.dataset.bookingStrings || '{}')
    } catch {
      strings = {}
    }
    const icons = {
      clock: this.dataset.modalIconClock || '',
      pin: this.dataset.modalIconPin || this.dataset.addressIcon || '',
      phone: this.dataset.modalIconPhone || this.dataset.phoneIcon || '',
      email: this.dataset.modalIconEmail || this.dataset.emailIcon || '',
      globe: this.dataset.modalIconGlobe || this.dataset.websiteIcon || '',
      calendar: this.dataset.modalIconCalendar || '',
      chevronLeft: this.dataset.modalIconChevronLeft || '',
      chevronRight: this.dataset.modalIconChevronRight || '',
      spin: this.dataset.modalIconSpin || '',
      checkCircle: this.dataset.modalIconCheckCircle || '',
      warning: this.dataset.modalIconWarning || '',
    }

    const initModal = (resolvedStrings) => {
      this.bookingModal = new ShowroomBookingModal({
        apiBase,
        store,
        locale,
        strings: resolvedStrings,
        icons,
        isEuStore: this.isEuStore,
      })
    }

    initModal(strings)

    if (!this.isEuStore || !apiBase || !store) {
      return
    }

    const url = `${apiBase.replace(/\/$/, '')}/api/showrooms/translations?store=${encodeURIComponent(store)}&locale=${encodeURIComponent(locale)}`
    fetch(url, { credentials: 'omit' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (this.bookingModal && data?.themeModal && typeof data.themeModal === 'object') {
          this.bookingModal.strings = {
            ...this.bookingModal.strings,
            ...data.themeModal,
          }
        }
      })
      .catch(() => {
        /* section settings fallback already loaded */
      })
  }

  setupBookingButtons () {
    try {
      const bookNowButtons = this.querySelectorAll('.showroom-booking__locations-list--action-book-now')
      bookNowButtons.forEach(button => {
        const showroomId = button.closest('.showroom-booking__locations-list--item')?.dataset.showroomId
        if (!showroomId) return
        const showroom = this.findShowroomByIdentifier(showroomId)
        if (!this.isBookingEnabled(showroom)) return
        button.onclick = (e) => {
          e.preventDefault()
          e.stopPropagation()
          this.openBookingModalForShowroom(showroom)
        }
      })
      const listItems = this.querySelectorAll('.showroom-booking__locations-list--item')
      listItems.forEach(item => {
        const showroomId = item.dataset.showroomId
        if (!showroomId) return
        const showroom = this.findShowroomByIdentifier(showroomId)
        if (!this.isBookingEnabled(showroom)) return
        item.style.cursor = 'pointer'
        item.addEventListener('click', (e) => {
          if (e.target.closest('button') || e.target.closest('a')) return
          this.openBookingModalForShowroom(showroom)
        })
      })
    } catch (error) {
      this.handleBookingUnavailable()
    }
  }

  isBookingEnabled (showroom) {
    if (!showroom) return false
    const slug = showroom.slug || showroom.booking_slug
    const enabled = showroom.booking_enabled
    if (enabled === false || enabled === 'false') return false
    return Boolean(slug)
  }

  openBookingModalForShowroom (showroom) {
    if (!this.bookingModal || !this.isBookingEnabled(showroom)) {
      this.handleBookingUnavailable()
      return
    }
    this.bookingModal.open(this.ensureShowroomDataIntegrity(showroom))
  }

  handleBookingUnavailable () {
    const bookNowButtons = this.querySelectorAll('.showroom-booking__locations-list--action-book-now')
    bookNowButtons.forEach(button => {
      button.classList.add('opacity-50', 'cursor-not-allowed')
      button.disabled = true
      const originalText = button.textContent
      button.innerHTML = `
        <span class="w-5 h-5 mr-2 inline-flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </span>
        Booking temporarily unavailable
      `
    })
  }

  connectedCallback () {
    this.debugShowroomData()
    this.initializeElements()
    this.initializeWithSortedShowrooms()
    this.setupBookingButtons()
    this.handleBookShowroomUrlParams()
    this.addEventListeners()
    this.initializeMobileNav()
    if (this.mapContainer) {
      this.ensureGoogleMaps()
        .then(() => {
          this.initializeMapWithUKBounds(window.showroomLocations || [])
        })
        .catch((error) => {
          console.error('Showroom map unavailable:', error)
        })
    }
    window.addEventListener('resize', () => {
      const currentWidth = window.innerWidth
      if (currentWidth === this.lastWindowWidth) {
        return
      }
      this.lastWindowWidth = currentWidth
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout)
      }
      this.resizeTimeout = setTimeout(() => {
        this.handleResponsiveLayout()
      }, 250)
    })
  }

  initializeElements () {
    this.postcodeForm = this.querySelector('.showroom-booking__postcode-form')
    this.postcodeInput = this.querySelector('.showroom-booking__postcode-input input')
    this.locationsContainer = this.querySelector('.showroom-booking__locations')
    this.mapContainer = this.querySelector('.showroom-booking__locations-map')
    this.listViewButton = this.querySelector('.showroom-booking__nav--list-view')
    this.mapViewButton = this.querySelector('.showroom-booking__nav--map-view')
    this.listViewColumn = this.querySelector('.showroom-booking__locations-column--list')
    this.mapViewColumn = this.querySelector('.showroom-booking__locations-column--map')
    if (this.searchInputType === 'postcode') {
      this.postcodeInput.addEventListener('input', (event) => {
        const start = event.target.selectionStart
        const end = event.target.selectionEnd
        event.target.value = event.target.value.toUpperCase()
        event.target.setSelectionRange(start, end)
        this.toggleClearButton()
      })
    } else {
      this.postcodeInput.addEventListener('input', () => {
        this.toggleClearButton()
      })
    }
    this.listWrapper = this.querySelector('.showroom-booking__locations-list-wrapper')
    this.listFader = this.querySelector('.showroom-booking__locations-list-fader')
    this.listFaderTop = this.querySelector('.showroom-booking__locations-list-fader-top')
    if (this.listWrapper && this.listFader) {
      this.listWrapper.addEventListener('scroll', this.handleScroll)
      this.updateFaderVisibility()
    }
    this.clearButton = this.querySelector('.showroom-booking__postcode-clear')
  }

  initializeMobileNav () {
    if (window.innerWidth < 1280) {
      this.listViewColumn.classList.remove('hidden')
      this.mapViewColumn.classList.add('hidden')
      this.listViewButton.classList.add('tb-timeline-link-active')
      this.mapViewButton.classList.remove('tb-timeline-link-active')
    }
  }

  addEventListeners () {
    this.postcodeForm.addEventListener('submit', (event) => {
      event.preventDefault()
      this.handlePostcodeSubmit()
    })
    this.listViewButton.addEventListener('click', () => {
      this.switchToView('list')
    })
    this.mapViewButton.addEventListener('click', () => {
      this.switchToView('map')
    })
    window.addEventListener('resize', () => {
      if (this.map) {
        google.maps.event.trigger(this.map, 'resize')
        this.fitMapBounds()
      }
    })
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        this.resetTool()
      })
    }
  }

  switchToView (view) {
    if (window.innerWidth < 1280) {
      this.currentView = view
      this.toggleViewColumns(view === 'list')
      if (view === 'map' && this.map) {
        google.maps.event.trigger(this.map, 'resize')
        if (this.searchResults && this.searchResults.length > 0) {
          this.zoomToClosestShowrooms(this.searchResults.slice(0, 3))
        } else if (this.isCountrySpecificMarket()) {
          const countryBounds = this.getCountryBounds(this.currentCountryCode)
          if (countryBounds) {
            this.map.fitBounds(countryBounds, MAP_BOUNDS_PADDING)
            setTimeout(() => {
              const currentZoom = this.map.getZoom()
              if (currentZoom > 7) {
                this.map.setZoom(7)
              }
            }, 100)
          }
        } else {
          this.fitMapBounds()
        }
      }
    }
  }

  toggleViewColumns (showList) {
    if (window.innerWidth < 1280) {
      this.listViewColumn.classList.toggle('hidden', !showList)
      this.mapViewColumn.classList.toggle('hidden', showList)
      this.listViewButton.classList.toggle('tb-timeline-link-active', showList)
      this.mapViewButton.classList.toggle('tb-timeline-link-active', !showList)
    }
  }

  async handlePostcodeSubmit () {
    if (this.loading) return
    this.loading = true
    const startTime = Date.now()
    const location = this.postcodeInput.value.trim()

    this.setAttribute('loading', '')
    if (!location) {
      await this.ensureMinimumLoadingTime(startTime)
      this.loading = false
      this.removeAttribute('loading')
      return
    }
    try {
      const locationData = await this.getLocationCoordinates(location)
      if (!locationData) throw new Error('Invalid location')

      this.showAllMarkers()
      const nearestShowrooms = this.findNearestShowrooms(locationData)

      if (locationData.isCountrySearch) {
        this.showroomMarkers.forEach(marker => {
          const showroomId = marker.showroomId
          const showroom = window.showroomLocations.find(s => String(s.id) === String(showroomId))
          if (showroom) {
            const explicit = (showroom.country_code || showroom.countryCode || '').toUpperCase()
            const showroomCountryCode = explicit || this.getCountryCodeFromRegion(showroom.region_handle)
            this.setMarkerMap(marker, showroomCountryCode === locationData.countryCode ? this.map : null)
          }
        })
      } else {
        this.showroomMarkers.forEach(marker => this.setMarkerMap(marker, this.map))
      }
      this.searchResults = nearestShowrooms
      if (this.closestShowroomsText) {
        this.closestShowroomsText.innerHTML = this.closestShowroomsText.innerHTML.replace('{{ postcode }}', `<strong>${location}</strong>`)
        this.closestShowroomsText.classList.remove('hidden')
      }
      await this.ensureGoogleMaps()
      this.initializeMap(locationData, nearestShowrooms)
      await this.ensureMinimumLoadingTime(startTime)

      this.showResults(nearestShowrooms)

      if (locationData.isCountrySearch && locationData.viewport) {
        this.map.fitBounds(locationData.viewport, MAP_BOUNDS_PADDING)
      } else {
        this.zoomToClosestShowrooms(nearestShowrooms.slice(0, 3))
      }
      if (this.locationsContainer && this.querySelector('.showroom-booking__postcode')) {
        this.locationsContainer.classList.remove('hidden')
        const postcodeElement = this.querySelector('.showroom-booking__postcode')
        postcodeElement.classList.remove('mb-[250px]')
      }
      this.loading = false
      this.removeAttribute('loading')
    } catch (error) {
      await this.ensureMinimumLoadingTime(startTime)
      this.loading = false
      this.removeAttribute('loading')
    }
  }

  async ensureMinimumLoadingTime (startTime) {
    const elapsedTime = Date.now() - startTime
    const remainingTime = Math.max(0, 800 - elapsedTime)
    if (remainingTime > 0) {
      await new Promise(resolve => setTimeout(resolve, remainingTime))
    }
  }

  async getLocationCoordinates (location) {
    await this.ensureGoogleMaps()
    const geocoder = new google.maps.Geocoder()
    try {
      let searchQuery = location
      let isCountrySearch = false
      const countryCode = this.getCountryCodeFromRegion(location)
      if (countryCode) {
        isCountrySearch = true
        searchQuery = location
      } else {
        const firstShowroom = window.showroomLocations?.[0]
        if (firstShowroom?.region_handle) {
          const countryCode = this.getCountryCodeFromRegion(firstShowroom.region_handle)
          if (countryCode) {
            if (this.searchInputType === 'postcode') {
              searchQuery = `${location}, ${countryCode}`
            } else {
              if (!location.toLowerCase().includes(countryCode.toLowerCase())) {
                searchQuery = `${location}, ${countryCode}`
              }
            }
          }
        }
      }
      const response = await geocoder.geocode({
        address: searchQuery,
        region: isCountrySearch ? countryCode : this.getCountryCodeFromRegion(window.showroomLocations?.[0]?.region_handle) || undefined,
      })
      if (response.results && response.results[0]) {
        const { lat, lng } = response.results[0].geometry.location
        return {
          lat: lat(),
          lng: lng(),
          isCountrySearch,
          countryCode: isCountrySearch ? countryCode : null,
          viewport: response.results[0].geometry.viewport,
        }
      }
      return null
    } catch (error) {
      return null
    }
  }

  getCountryCodeFromRegion (region) {
    // Accept both handles (lowercase slugs) and English names for backward compatibility
    const regionToCountry = {
      // Handles (lowercase slugs)
      england: 'GB',
      scotland: 'GB',
      wales: 'GB',
      'northern-ireland': 'GB',
      france: 'FR',
      germany: 'DE',
      italy: 'IT',
      spain: 'ES',
      netherlands: 'NL',
      belgium: 'BE',
      austria: 'AT',
      switzerland: 'CH',
      denmark: 'DK',
      sweden: 'SE',
      norway: 'NO',
      finland: 'FI',
      ireland: 'IE',
      portugal: 'PT',
      greece: 'GR',
      poland: 'PL',
      'czech-republic': 'CZ',
      slovakia: 'SK',
      hungary: 'HU',
      romania: 'RO',
      bulgaria: 'BG',
      croatia: 'HR',
      slovenia: 'SI',
      estonia: 'EE',
      latvia: 'LV',
      lithuania: 'LT',
      cyprus: 'CY',
      malta: 'MT',
      luxembourg: 'LU',
      iceland: 'IS',
      liechtenstein: 'LI',
      // English names for backward compatibility
      England: 'GB',
      Scotland: 'GB',
      Wales: 'GB',
      'Northern Ireland': 'GB',
      France: 'FR',
      Germany: 'DE',
      Italy: 'IT',
      Spain: 'ES',
      Netherlands: 'NL',
      Belgium: 'BE',
      Austria: 'AT',
      Switzerland: 'CH',
      Denmark: 'DK',
      Sweden: 'SE',
      Norway: 'NO',
      Finland: 'FI',
      Ireland: 'IE',
      Portugal: 'PT',
      Greece: 'GR',
      Poland: 'PL',
      'Czech Republic': 'CZ',
      Slovakia: 'SK',
      Hungary: 'HU',
      Romania: 'RO',
      Bulgaria: 'BG',
      Croatia: 'HR',
      Slovenia: 'SI',
      Estonia: 'EE',
      Latvia: 'LV',
      Lithuania: 'LT',
      Cyprus: 'CY',
      Malta: 'MT',
      Luxembourg: 'LU',
      Iceland: 'IS',
      Liechtenstein: 'LI',
    }
    return regionToCountry[region] || null
  }

  isCountrySpecificMarket () {
    // Only apply country-specific behavior for FR, DE, and IT markets
    // The default EU store should use alphabetical ordering and default centering
    return this.isEuStore && this.currentCountryCode && ['FR', 'DE', 'IT'].includes(this.currentCountryCode)
  }

  getCountryBounds (countryCode) {
    const countryBounds = {
      GB: { north: 58.6350001, south: 50.0000000, west: -10.5500000, east: 1.7800000 },
      FR: { north: 51.1242, south: 41.3253, west: -5.5591, east: 9.6625 },
      DE: { north: 55.0815, south: 47.2701, west: 5.8663, east: 15.0419 },
      IT: { north: 47.0921, south: 35.4929, west: 6.6267, east: 18.7975 },
      ES: { north: 43.7913, south: 35.1736, west: -9.3014, east: 4.3278 },
      NL: { north: 53.6756, south: 50.7504, west: 3.3316, east: 7.2275 },
      BE: { north: 51.5051, south: 49.4969, west: 2.5465, east: 6.4078 },
      AT: { north: 49.0211, south: 46.3722, west: 9.5307, east: 17.1608 },
      CH: { north: 47.8084, south: 45.8179, west: 5.9559, east: 10.4922 },
      DK: { north: 57.7513, south: 54.5594, west: 8.0756, east: 15.1588 },
      SE: { north: 69.0599, south: 55.3617, west: 11.0273, east: 24.1773 },
      NO: { north: 71.1853, south: 57.9594, west: 4.6065, east: 31.2935 },
      FI: { north: 70.0922, south: 59.8084, west: 20.5418, east: 31.5867 },
      IE: { north: 55.4360, south: 51.4166, west: -10.4800, east: -5.9917 },
      PT: { north: 42.1543, south: 36.9382, west: -9.5261, east: -6.1891 },
      GR: { north: 41.7488, south: 34.8021, web: 19.3736, east: 28.2336 },
      PL: { north: 54.8356, south: 49.0020, west: 14.1229, east: 24.1458 },
    }
    return countryBounds[countryCode] || null
  }

  findNearestShowrooms (postcodeCoords) {
    const allLocations = window.showroomLocations || []

    const showroomsWithDistance = allLocations.map(location => {
      const distance = this.calculateDistance(postcodeCoords, {
        lat: parseFloat(location.pin_lat),
        lng: parseFloat(location.pin_long),
      })

      return {
        ...location,
        pinLat: parseFloat(location.pin_lat),
        pinLong: parseFloat(location.pin_long),
        distance,
      }
    })

    const sortedShowrooms = showroomsWithDistance.sort((a, b) => a.distance - b.distance)

    return sortedShowrooms
  }

  initializeMap (center, showrooms) {
    const currentZoom = this.map ? this.map.getZoom() : 8
    if (!this.map) {
      this.map = new google.maps.Map(this.mapContainer, this.getBaseMapOptions({
        zoom: 5,
        center,
      }))
      this.map.addListener('zoom_changed', () => {
        this.currentZoom = this.map.getZoom()
        this.userChangedZoom = true
      })
      this.map.addListener('click', () => {
        if (this.currentInfoWindow) {
          this.currentInfoWindow.setMap(null)
        }
      })
    }
    this.markers.forEach(marker => this.setMarkerMap(marker, null))
    this.markers.clear()
    this.showroomMarkers = []
    if (this.currentInfoWindow) {
      this.currentInfoWindow.setMap(null)
    }
    this.bounds = new google.maps.LatLngBounds()
    showrooms.forEach((showroom, index) => {
      const position = {
        lat: parseFloat(showroom.pin_lat),
        lng: parseFloat(showroom.pin_long),
      }
      if (isNaN(position.lat) || isNaN(position.lng)) return
      const marker = this.createMarker(position, showroom, showroom.id, index)
      this.setMarkerMap(marker, this.map)
      marker.showroomId = String(showroom.id)
      this.markers.set(showroom.id, marker)
      this.showroomMarkers.push(marker)
      this.bounds.extend(position)
    })
  }

  createMarker (position, showroom, showroomId, index) {
    const img = document.createElement('img')
    img.src = this.dataset.mapMarker
    img.width = 40
    img.height = 49
    img.style.display = 'block'
    img.alt = ''

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position,
      map: null,
      content: img,
      gmpClickable: true,
    })
    marker.showroomId = String(showroomId)
    marker.regionName = showroom.region_name || 'No Region'
    marker.index = index
    marker.addEventListener('gmp-click', () => {
      if (this.currentInfoWindow) {
        this.currentInfoWindow.setMap(null)
      }
      const InfoWindowClass = this.createCustomInfoWindow(position, showroom)
      this.currentInfoWindow = new InfoWindowClass(
        this.decodeHtmlEntities,
        this.getCountryFlag.bind(this),
        this.bookLabel,
        this.isBookingEnabled.bind(this),
      )
      this.currentInfoWindow.setMap(this.map)

      // Position the marker lower in viewport to ensure info window is fully visible
      this.panToShowInfoWindow(position)
    })
    return marker
  }

  fitMapBounds () {
    if (this.bounds && this.map) {
      if (!this.hasZoomedToMarker) {
        this.map.fitBounds(this.bounds, MAP_BOUNDS_PADDING)
        setTimeout(() => {
          const zoom = this.map.getZoom()
          if (zoom && zoom > 12) {
            this.map.setZoom(12)
          }
        }, 100)
      }
    }
  }

  calculateDistance (point1, point2) {
    const lat1 = this.toRadians(point1.lat)
    const lat2 = this.toRadians(point2.lat)
    const dLat = this.toRadians(point2.lat - point1.lat)
    const dLon = this.toRadians(point2.lng - point1.lng)
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const earthRadius = this.isEuStore ? EARTH_RADIUS_KM : EARTH_RADIUS_MILES
    return Math.round(earthRadius * c)
  }

  formatDistanceText (distance) {
    return this.isEuStore ? `${distance} km` : `${distance} miles`
  }

  toRadians (degrees) {
    return degrees * (Math.PI / 180)
  }

  showResults (nearestShowrooms) {
    const listContainer = this.querySelector('.showroom-booking__locations-list')
    if (!listContainer) return

    this.showroomMarkers.forEach(marker => {
      const showroomId = marker.showroomId
      const isInResults = nearestShowrooms.some(showroom => String(showroom.id) === String(showroomId))
      this.setMarkerMap(marker, isInResults ? this.map : null)
    })
    const locationItems = listContainer.querySelectorAll('.showroom-booking__locations-list--item')

    locationItems.forEach((item, index) => {
      const showroom = nearestShowrooms[index]

      // Ensure showroom data integrity
      const cleanShowroom = showroom ? this.ensureShowroomDataIntegrity(showroom) : null

      if (cleanShowroom) {
        item.style.display = ''

        // Store the clean showroom data on the element for later use
        item._showroomData = cleanShowroom

        const locationText = item.querySelector('.showroom-booking__locations-list--location')
        if (locationText) {
          const displayName = this.decodeHtmlEntities(cleanShowroom.closestCity || cleanShowroom.town || cleanShowroom.companyName)
          const shouldShowFlag = this.isEuStore
          // Prefer the explicit `country_code` from the toolkit-managed
          // metaobject; fall back to the legacy region-handle mapping.
          const countryCode = shouldShowFlag
            ? (cleanShowroom.countryCode || this.getCountryCodeFromRegion(cleanShowroom.regionHandle))
            : ''
          const flag = shouldShowFlag && countryCode ? this.getCountryFlag(countryCode) : ''
          const countryName = countryCode && window.Tentbox?.translations?.countries?.[countryCode.toLowerCase()] ? window.Tentbox.translations.countries[countryCode.toLowerCase()] : ''
          locationText.innerHTML = `${displayName} Showroom${flag ? `<div class="text-base font-normal mt-0.5">${flag} ${countryName}</div>` : ''}`
        }
        const distanceText = item.querySelector('.showroom-booking__locations-column--distance')
        if (distanceText) {
          distanceText.textContent = this.formatDistanceText(cleanShowroom.distance)
        }

        // Update contact information if additional info is enabled
        if (this.showAdditionalInfo) {
          this.renderContactInfo(item, cleanShowroom)
        }

        // Clear any existing event listeners by cloning the item
        const newItem = item.cloneNode(true)
        item.parentNode.replaceChild(newItem, item)

        // Re-store the data on the new element
        newItem._showroomData = cleanShowroom

        // Set up booking functionality with clean data
        if (this.isBookingEnabled(cleanShowroom)) {
          newItem.style.cursor = 'pointer'
          const displayName = this.decodeHtmlEntities(cleanShowroom.closestCity || cleanShowroom.town || cleanShowroom.companyName)
          newItem.setAttribute('data-tracking', `showroom-locations-booking_${displayName}`)

          // Card click handler
          newItem.onclick = (e) => {
            if (e.target.closest('button') || e.target.closest('a')) return
            this.openBookingModalForShowroom(cleanShowroom)
          }

          // Book now button click handler
          const bookNowButton = newItem.querySelector('.showroom-booking__locations-list--action-book-now')
          if (bookNowButton) {
            bookNowButton.setAttribute('data-tracking', `showroom-locations-booking_${displayName}`)
            bookNowButton.onclick = (e) => {
              e.preventDefault()
              e.stopPropagation()
              this.openBookingModalForShowroom(cleanShowroom)
              return false
            }
          }
        }
      } else {
        item.style.display = 'none'
      }
    })
  }

  renderContactInfo (item, showroom) {
    const container = item.querySelector('.showroom-booking__locations-list--contact-info')
    if (!container) return

    const rows = []
    const addressParts = []
    if (showroom.streetAddress) addressParts.push(this.decodeHtmlEntities(showroom.streetAddress))
    if (showroom.town) addressParts.push(this.decodeHtmlEntities(showroom.town))
    if (showroom.postcode) addressParts.push(this.decodeHtmlEntities(showroom.postcode))
    const fullAddress = addressParts.join(', ')

    if (fullAddress) {
      rows.push(`
        <div class="showroom-booking__contact-info--container flex gap-2">
          <div class="w-5 h-5 shrink-0">${this.addressIcon}</div>
          <div class="grow">
            <span class="showroom-booking__contact-info--address-text text-sm">${escapeHtml(fullAddress)}</span>
          </div>
        </div>
      `)
    }

    if (showroom.phone) {
      rows.push(`
        <div class="showroom-booking__contact-info--container flex gap-2">
          <div class="w-5 h-5 shrink-0">${this.phoneIcon}</div>
          <div class="grow">
            <a href="tel:${escapeAttr(showroom.phone)}" class="showroom-booking__contact-info--phone text-sm underline underline-offset-2 hover:no-underline">${escapeHtml(showroom.phone)}</a>
          </div>
        </div>
      `)
    }

    if (showroom.email) {
      const email = this.decodeHtmlEntities(showroom.email)
      rows.push(`
        <div class="showroom-booking__contact-info--container flex gap-2">
          <div class="w-5 h-5 shrink-0">${this.emailIcon}</div>
          <div class="grow">
            <a href="mailto:${escapeAttr(email)}" class="showroom-booking__contact-info--email text-sm underline underline-offset-2 hover:no-underline">${escapeHtml(email)}</a>
          </div>
        </div>
      `)
    }

    if (showroom.website) {
      const website = this.decodeHtmlEntities(showroom.website)
      const websitePretty = this.decodeHtmlEntities(showroom.websitePretty || showroom.website)
      rows.push(`
        <div class="showroom-booking__contact-info--container flex gap-2">
          <div class="w-5 h-5 shrink-0">${this.websiteIcon}</div>
          <div class="grow">
            <a href="${escapeAttr(website)}" target="_blank" rel="noopener noreferrer" class="showroom-booking__contact-info--website text-sm underline underline-offset-2 hover:no-underline">${escapeHtml(websitePretty)}</a>
          </div>
        </div>
      `)
    }

    container.innerHTML = rows.join('')
  }

  logCurrentCardStates () {
    // This method is kept for potential future debugging but no longer logs
  }

  set loading (value) {
    this.postcodeForm.setAttribute('loading', value)

    // Also apply loading state to contact information elements
    if (value === 'true') {
      this.setAttribute('loading', '')
    } else {
      this.removeAttribute('loading')
    }
  }

  get loading () {
    return this.postcodeForm.getAttribute('loading') === 'true'
  }

  updateLocationsList () {
    const decoder = document.createElement('div')
    const locationItems = this.querySelectorAll('.showroom-booking__locations-list--item')
    const visibleShowrooms = []
    let visibleItemCount = 0
    locationItems.forEach(item => {
      decoder.innerHTML = item.dataset.regionName
      const showroomId = item.dataset.showroomId
      if (isVisible) {
        item.classList.remove('hidden')
        visibleItemCount++
        const showroom = this.findShowroomByIdentifier(showroomId)
        if (showroom) {
          visibleShowrooms.push(showroom)
        }
      } else {
        item.classList.add('hidden')
      }
    })
    if (!this.searchResults) {
      this.resetMapToUK()
    }
  }

  findShowroomByIdentifier (identifier) {
    if (!window.showroomLocations) return null
    if (this.showroomIdProperty) {
      const showroom = window.showroomLocations.find(d =>
        String(d[this.showroomIdProperty]) === String(identifier),
      )
      if (showroom) return showroom
    }
    if (identifier && identifier.startsWith('showroom-')) {
      const index = parseInt(identifier.split('-')[1], 10)
      if (!isNaN(index) && index < window.showroomLocations.length) {
        return window.showroomLocations[index]
      }
    }
    return null
  }

  resetMapToUK () {
    if (!this.map) return
    let bounds = {
      north: 58.6350001,
      south: 50.0000000,
      west: -10.5500000,
      east: 1.7800000,
    }
    if (this.isCountrySpecificMarket()) {
      const countryBounds = this.getCountryBounds(this.currentCountryCode)
      if (countryBounds) {
        bounds = countryBounds
      }
    }
    this.map.fitBounds(bounds)
    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      const maxZoom = this.isCountrySpecificMarket() ? 7 : 6
      if (this.map.getZoom() > maxZoom) return
      this.map.setZoom(Math.min(this.map.getZoom() + 1, maxZoom))
    })
  }

  initializeWithSortedShowrooms () {
    const allShowrooms = window.showroomLocations || []

    let sortedShowrooms

    // Apply market-prioritized sorting for FR and DE markets
    if (this.currentCountryCode === 'FR' || this.currentCountryCode === 'DE') {
      const targetRegionHandle = this.currentCountryCode === 'FR' ? 'france' : 'germany'

      const marketShowrooms = []
      const otherShowrooms = []

      allShowrooms.forEach(showroom => {
        if (showroom.region_handle === targetRegionHandle) {
          marketShowrooms.push(showroom)
        } else {
          otherShowrooms.push(showroom)
        }
      })

      // Sort each group alphabetically
      const sortAlphabetically = (a, b) => {
        const nameA = a.closest_city || a.town || a.company_name
        const nameB = b.closest_city || b.town || b.company_name
        return nameA.localeCompare(nameB)
      }

      marketShowrooms.sort(sortAlphabetically)
      otherShowrooms.sort(sortAlphabetically)

      sortedShowrooms = [...marketShowrooms, ...otherShowrooms]
    } else {
      // Default alphabetical sorting for all other markets
      sortedShowrooms = allShowrooms.sort((a, b) => {
        const nameA = a.closest_city || a.town || a.company_name
        const nameB = b.closest_city || b.town || b.company_name
        return nameA.localeCompare(nameB)
      })
    }

    if (!this.showroomIdProperty) {
      const firstShowroom = sortedShowrooms[0]
      if (firstShowroom) {
        this.showroomIdProperty = 'company_name'
      }
    }
    const locationItems = this.querySelectorAll('.showroom-booking__locations-list--item')

    locationItems.forEach((item, index) => {
      const showroom = sortedShowrooms[index]
      if (!showroom) {
        item.classList.add('hidden')
        return
      }
      const showroomId = showroom[this.showroomIdProperty] || `showroom-${index}`
      item.dataset.showroomId = showroomId
      item.dataset.region = showroom.region || ''
      const regionName = showroom.region_name || 'No Region'
      item.dataset.regionName = regionName

      const locationContainer = item.querySelector('.showroom-booking__locations-list--location')
      if (locationContainer) {
        const displayName = this.decodeHtmlEntities(showroom.closest_city || showroom.town || showroom.company_name)
        const shouldShowFlag = this.isEuStore
        // Prefer the explicit `country_code` from the toolkit-managed
        // metaobject; fall back to the legacy region-handle mapping.
        const rawCountryCode = showroom.country_code || showroom.countryCode || ''
        const countryCode = shouldShowFlag
          ? (rawCountryCode ? rawCountryCode.toUpperCase() : this.getCountryCodeFromRegion(showroom.region_handle))
          : ''
        const flag = shouldShowFlag && countryCode ? this.getCountryFlag(countryCode) : ''
        const countryName = countryCode && window.Tentbox?.translations?.countries?.[countryCode.toLowerCase()] ? window.Tentbox.translations.countries[countryCode.toLowerCase()] : ''
        locationContainer.innerHTML = `${displayName} Showroom${flag ? `<div class="text-base font-normal mt-0.5">${flag} ${countryName}</div>` : ''}`
      }

      // Initialize contact info with actual data
      if (this.showAdditionalInfo) {
        const cleanShowroom = this.ensureShowroomDataIntegrity(showroom)
        this.renderContactInfo(item, cleanShowroom)
      }

      if (this.isBookingEnabled(showroom)) {
        item.style.cursor = 'pointer'
        const displayName = this.decodeHtmlEntities(showroom.closest_city || showroom.town || showroom.company_name)
        item.setAttribute('data-tracking', `showroom-locations-booking_${displayName}`)
        item.onclick = (e) => {
          if (e.target.closest('button') || e.target.closest('a')) return
          this.openBookingModalForShowroom(showroom)
        }
      }
      const calenderLinkButton = item.querySelector('.showroom-booking__locations-list--action-book-now')
      if (calenderLinkButton && this.isBookingEnabled(showroom)) {
        const displayName = this.decodeHtmlEntities(showroom.closest_city || showroom.town || showroom.company_name)
        calenderLinkButton.setAttribute('data-tracking', `showroom-locations-booking_${displayName}`)
        calenderLinkButton.addEventListener('click', (e) => {
          e.preventDefault()
          e.stopPropagation()
          this.openBookingModalForShowroom(showroom)
          return false
        })
      }
    })
  }

  initializeMapWithUKBounds (showrooms) {
    const mapCenter = { lat: this.mapCenterCoordinates[0], lng: this.mapCenterCoordinates[1] }
    const mapZoom = this.initialZoomLevel

    this.map = new google.maps.Map(this.mapContainer, this.getBaseMapOptions({
      center: mapCenter,
      zoom: mapZoom,
    }))
    this.map.addListener('zoom_changed', () => {
      this.currentZoom = this.map.getZoom()
      this.userChangedZoom = true
    })
    this.map.addListener('click', () => {
      if (this.currentInfoWindow) {
        this.currentInfoWindow.setMap(null)
      }
    })
    this.hasZoomedToMarker = false
    if (this.showroomMarkers && this.showroomMarkers.length) {
      this.showroomMarkers.forEach(marker => this.setMarkerMap(marker, null))
    }
    this.showroomMarkers = []
    this.markers = new Map()
    showrooms.forEach((showroom, index) => {
      if (showroom.pin_lat && showroom.pin_long) {
        const position = {
          lat: parseFloat(showroom.pin_lat),
          lng: parseFloat(showroom.pin_long),
        }
        if (isNaN(position.lat) || isNaN(position.lng)) return
        const showroomId = showroom[this.showroomIdProperty] || `showroom-${index}`
        const marker = this.createMarker(position, showroom, showroomId, index)
        this.setMarkerMap(marker, this.map)
        this.showroomMarkers.push(marker)
        this.markers.set(showroomId, marker)
      }
    })
  }

  handleScroll () {
    this.updateFaderVisibility()
  }

  updateFaderVisibility () {
    if (!this.listWrapper || !this.listFader || !this.listFaderTop || window.innerWidth < 1280) return
    const isScrollable = this.listWrapper.scrollHeight > this.listWrapper.clientHeight
    const isAtBottom = Math.ceil(this.listWrapper.scrollTop + this.listWrapper.clientHeight) >= this.listWrapper.scrollHeight
    const isAtTop = this.listWrapper.scrollTop <= 0
    if (!isScrollable || isAtBottom) {
      this.listFader.style.opacity = '0'
    } else {
      this.listFader.style.opacity = '1'
    }
    if (!isScrollable || isAtTop) {
      this.listFaderTop.style.opacity = '0'
    } else {
      this.listFaderTop.style.opacity = '1'
    }
  }

  handleResponsiveLayout () {
    const isDesktop = window.innerWidth >= 1280
    if (isDesktop) {
      this.listViewColumn.classList.remove('hidden')
      this.mapViewColumn.classList.remove('hidden')
      if (this.map) {
        google.maps.event.trigger(this.map, 'resize')
        this.fitMapBounds()
      }
    } else {
      const isListVisible = !this.listViewColumn.classList.contains('hidden')
      const isMapVisible = !this.mapViewColumn.classList.contains('hidden')
      if (isMapVisible || !isListVisible) {
        this.initializeMobileNav()
      }
    }
  }

  disconnectedCallback () {
    if (this.listWrapper) {
      this.listWrapper.removeEventListener('scroll', this.handleScroll)
    }
  }

  createCustomInfoWindow (position, showroom) {
    class CustomInfoWindow extends google.maps.OverlayView {
      constructor (decodeHtmlEntities, getCountryFlag, bookLabel, isBookingEnabled) {
        super()
        this.position = position
        this.showroom = showroom
        this.decodeHtmlEntities = decodeHtmlEntities
        this.getCountryFlag = getCountryFlag
        this.bookLabel = bookLabel
        this.isBookingEnabled = isBookingEnabled
      }

      onAdd () {
        const div = document.createElement('div')
        div.className = 'showroom-booking__info-window-container'
        div.style.position = 'absolute'
        div.style.zIndex = '1000'

        const pointer = document.createElement('div')
        pointer.className = 'showroom-booking__info-window--pointer absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white rotate-45'

        const bookNowLabel = document.querySelector('showroom-booking').getAttribute('data-book-now-label')
        const rawDisplayName = this.showroom.closest_city || this.showroom.town || this.showroom.company_name
        const displayName = this.decodeHtmlEntities(rawDisplayName)

        // Get the showroom booking element to access settings and icons
        const showroomBookingElement = document.querySelector('showroom-booking')
        const showAdditionalInfo = showroomBookingElement?.dataset.showAdditionalInfo === 'true'

        // Build contact info
        let contactInfoHtml = ''
        if (showAdditionalInfo) {
          const phoneIcon = showroomBookingElement?.dataset.phoneIcon || ''
          const addressIcon = showroomBookingElement?.dataset.addressIcon || ''
          const emailIcon = showroomBookingElement?.dataset.emailIcon || ''
          const websiteIcon = showroomBookingElement?.dataset.websiteIcon || ''

          contactInfoHtml = '<div class="flex flex-col gap-2 mt-2 pt-4">'

          // Address
          if (this.showroom.street_address || this.showroom.town || this.showroom.postcode) {
            const addressParts = []
            if (this.showroom.street_address) addressParts.push(this.decodeHtmlEntities(this.showroom.street_address))
            if (this.showroom.town) addressParts.push(this.decodeHtmlEntities(this.showroom.town))
            if (this.showroom.postcode) addressParts.push(this.decodeHtmlEntities(this.showroom.postcode))
            const fullAddress = addressParts.join(', ')
            const copyIcon = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5A3.375 3.375 0 0 0 6.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0 0 15 2.25h-1.5a2.251 2.251 0 0 0-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 0 0-9-9Z" /></svg>`
            contactInfoHtml += `
              <div class="flex gap-2">
                <div class="w-5 h-5 shrink-0">${addressIcon}</div>
                <div class="flex-grow flex justify-between items-start gap-2 min-w-0">
                  <span class="text-sm break-words flex-grow">${fullAddress}</span>
                  <button class="showroom-booking__copy-address w-4 h-4 shrink-0 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer" 
                          data-address="${fullAddress.replace(/"/g, '&quot;')}" 
                          title="Copy address">
                    ${copyIcon}
                  </button>
                </div>
              </div>
            `
          }

          // Phone
          if (this.showroom.phone) {
            contactInfoHtml += `
              <div class="flex gap-2">
                <div class="w-5 h-5 shrink-0">${phoneIcon}</div>
                <div class="flex-grow min-w-0">
                  <a href="tel:${this.showroom.phone}" class="text-sm underline underline-offset-2 hover:no-underline break-all">${this.showroom.phone}</a>
                </div>
              </div>
            `
          }

          // Email
          if (this.showroom.email) {
            contactInfoHtml += `
              <div class="flex gap-2">
                <div class="w-5 h-5 shrink-0">${emailIcon}</div>
                <div class="flex-grow min-w-0">
                  <a href="mailto:${this.showroom.email}" class="text-sm underline underline-offset-2 hover:no-underline break-all">${this.decodeHtmlEntities(this.showroom.email)}</a>
                </div>
              </div>
            `
          }

          // Website
          if (this.showroom.website) {
            contactInfoHtml += `
              <div class="flex gap-2">
                <div class="w-5 h-5 shrink-0">${websiteIcon}</div>
                <div class="flex-grow min-w-0">
                  <a href="${this.showroom.website}" target="_blank" rel="noopener noreferrer" class="text-sm underline underline-offset-2 hover:no-underline break-all">
                    ${this.decodeHtmlEntities(this.showroom.website_pretty || this.showroom.website)}
                  </a>
                </div>
              </div>
            `
          }

          contactInfoHtml += '</div>'
        }

        const content = document.createElement('div')
        content.className = 'showroom-booking__info-window relative p-5 bg-white rounded-lg shadow-lg min-w-[280px]'

        // Set maximum width based on map container width
        const mapDiv = this.getMap().getDiv()
        const mapWidth = mapDiv.offsetWidth
        const maxWidth = Math.min(320, mapWidth - 40) // Max 320px or map width minus 40px padding
        content.style.maxWidth = `${maxWidth}px`
        content.style.width = 'auto'
        content.innerHTML = `
            <button class="showroom-booking__info-window--close absolute top-3 right-3 w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div class="flex flex-col overflow-hidden">
              <div class="text-xl font-semibold pr-8 break-words">${displayName} Showroom</div>
              ${contactInfoHtml}
              <div class="mt-5">
                ${this.bookLabel ? `<div class="showroom-booking__info-window--book-now-label text-base mb-2 break-words">${this.bookLabel}</div>` : ''}
                <button class="showroom-booking__info-window--book-now button button-orange h-10 w-full text-sm min-w-0">
                  <span class="w-5 h-5 mr-2 inline-flex items-center justify-center shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z" />
                      </svg>
                  </span>
                  <span class="truncate">${bookNowLabel}</span>
                </button>
              </div>
            </div>
          `

        // Add click handler for the close button
        const closeButton = content.querySelector('.showroom-booking__info-window--close')
        const handleClose = (e) => {
          e.preventDefault()
          e.stopPropagation()
          e.stopImmediatePropagation()
          this.setMap(null)
          return false
        }
        closeButton.addEventListener('click', handleClose, { passive: false })
        closeButton.addEventListener('touchend', handleClose, { passive: false })

        // Add click handler for the book now button
        const bookNowButton = content.querySelector('.showroom-booking__info-window--book-now')
        if (bookNowButton && this.isBookingEnabled(this.showroom)) {
          const handleBooking = (e) => {
            e.preventDefault()
            e.stopPropagation()
            e.stopImmediatePropagation()
            const host = document.querySelector('showroom-booking')
            if (host) {
              host.openBookingModalForShowroom(this.showroom)
            }
            return false
          }

          bookNowButton.addEventListener('click', handleBooking, { passive: false })
          bookNowButton.addEventListener('touchend', handleBooking, { passive: false })
        }

        // Add click handler for copy address button
        const copyAddressButton = content.querySelector('.showroom-booking__copy-address')
        if (copyAddressButton) {
          copyAddressButton.addEventListener('click', async (e) => {
            e.stopPropagation()
            const address = copyAddressButton.dataset.address
            const copyIcon = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5A3.375 3.375 0 0 0 6.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0 0 15 2.25h-1.5a2.251 2.251 0 0 0-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 0 0-9-9Z" /></svg>`
            try {
              if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(address)
              } else {
                // Fallback for older browsers or non-secure contexts
                const textArea = document.createElement('textarea')
                textArea.value = address
                textArea.style.position = 'fixed'
                textArea.style.left = '-999999px'
                textArea.style.top = '-999999px'
                document.body.appendChild(textArea)
                textArea.focus()
                textArea.select()
                document.execCommand('copy')
                textArea.remove()
              }

              // Visual feedback
              const originalHTML = copyAddressButton.innerHTML
              copyAddressButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>`
              copyAddressButton.classList.add('text-green-600')
              setTimeout(() => {
                copyAddressButton.innerHTML = originalHTML
                copyAddressButton.classList.remove('text-green-600')
              }, 2000)
            } catch (error) {
              // Visual feedback for error
              const originalHTML = copyAddressButton.innerHTML
              copyAddressButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>`
              copyAddressButton.classList.add('text-red-600')
              setTimeout(() => {
                copyAddressButton.innerHTML = originalHTML
                copyAddressButton.classList.remove('text-red-600')
              }, 2000)
            }
          })
        }

        div.appendChild(content)
        div.appendChild(pointer)
        this.div = div
        const panes = this.getPanes()
        panes.floatPane.appendChild(div)
      }

      draw () {
        const overlayProjection = this.getProjection()
        const position = overlayProjection.fromLatLngToDivPixel(this.position)
        const div = this.div
        if (position) {
          // Get actual dimensions of the info window
          const rect = div.getBoundingClientRect()
          const windowWidth = rect.width || 280 // fallback to min-width
          const windowHeight = rect.height || 160 // reasonable fallback

          // Center horizontally and position above the marker
          div.style.left = `${position.x - windowWidth / 2}px`
          div.style.top = `${position.y - windowHeight - 55}px` // 55px accounts for marker height + spacing
        }
      }

      onRemove () {
        if (this.div) {
          this.div.parentNode.removeChild(this.div)
          this.div = null
        }
      }
    }

    return CustomInfoWindow
  }

  toggleClearButton () {
    if (!this.clearButton) return
    if (this.postcodeInput.value) {
      this.clearButton.classList.remove('hidden')
      this.clearButton.classList.add('flex')
    } else {
      this.clearButton.classList.add('hidden')
      this.clearButton.classList.remove('flex')
    }
  }

  resetTool () {
    this.postcodeInput.value = ''
    this.toggleClearButton()
    this.searchResults = null
    this.initializeWithSortedShowrooms()
    const distanceElements = this.querySelectorAll('.showroom-booking__locations-column--distance')
    distanceElements.forEach(element => {
      element.textContent = ''
    })
    if (this.map) {
      this.resetMarkersToDefault()
      this.resetMapToUK()
    }
    this.hasZoomedToMarker = false
    if (this.closestShowroomsText) {
      this.closestShowroomsText.classList.add('hidden')
      this.closestShowroomsText.innerHTML = this.closestShowroomsText.innerHTML.replace(/<strong>[A-Z0-9\s]+<\/strong>/g, '{{ postcode }}')
    }
    this.loading = false
    this.removeAttribute('loading')
  }

  panToShowInfoWindow (markerPosition) {
    if (!this.map) return

    // Get map container height to calculate offset
    const mapDiv = this.map.getDiv()
    const mapHeight = mapDiv.offsetHeight
    const isMobile = window.innerWidth < 768

    // Calculate pixel offset based on additional info and device type
    let offsetPercentage = 0.15 // Default 15% of map height

    if (this.showAdditionalInfo) {
      // Increase offset when additional contact info is showing
      offsetPercentage = isMobile ? 0.35 : 0.25 // 35% on mobile, 25% on desktop
    } else {
      // Standard offset for basic info
      offsetPercentage = isMobile ? 0.25 : 0.15 // 25% on mobile, 15% on desktop
    }

    const pixelOffset = Math.floor(mapHeight * offsetPercentage)

    // Calculate the final center position by offsetting from marker position
    const bounds = this.map.getBounds()
    const projection = this.map.getProjection()

    if (bounds && projection) {
      // Convert current bounds to get the lat/lng per pixel ratio
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()
      const latRange = ne.lat() - sw.lat()
      const latPerPixel = latRange / mapHeight

      // Calculate the offset in latitude (move north to position marker lower)
      const latOffset = (pixelOffset * latPerPixel)

      // Calculate final center position
      const finalCenter = {
        lat: markerPosition.lat + latOffset,
        lng: markerPosition.lng,
      }

      // Pan to the calculated position in one smooth motion
      this.map.panTo(finalCenter)
    } else {
      // Fallback to simple pan if projection/bounds not available
      this.map.panTo(markerPosition)
    }
  }

  zoomToClosestShowrooms (showrooms) {
    if (!showrooms || showrooms.length === 0 || !this.map) return
    const bounds = new google.maps.LatLngBounds()
    showrooms.forEach(showroom => {
      const position = {
        lat: parseFloat(showroom.pin_lat),
        lng: parseFloat(showroom.pin_long),
      }
      if (!isNaN(position.lat) && !isNaN(position.lng)) {
        bounds.extend(position)
      }
    })
    this.map.panToBounds(bounds, MAP_BOUNDS_PADDING)
    this.map.fitBounds(bounds, MAP_BOUNDS_PADDING)
    setTimeout(() => {
      const currentZoom = this.map.getZoom()
      let targetZoom = currentZoom
      if (this.searchInputType === 'address') {
        targetZoom = Math.max(1, currentZoom - 1)
      } else {
        targetZoom = Math.min(currentZoom, 12)
      }
      this.map.setZoom(targetZoom)
      this.hasZoomedToMarker = true
    }, 100)
    this.userChangedZoom = false
  }

  zoomToFilteredShowrooms (visibleShowrooms) {
    if (!this.map) return
    const bounds = new google.maps.LatLngBounds()
    let markerCount = 0
    this.showroomMarkers.forEach(marker => {
      if (this.isMarkerVisible(marker)) {
        const position = this.getMarkerPosition(marker)
        if (position) {
          bounds.extend(position)
        }
        markerCount++
      }
    })
    if (markerCount > 0) {
      setTimeout(() => {
        this.map.fitBounds(bounds, MAP_BOUNDS_PADDING)
        setTimeout(() => {
          const zoom = this.map.getZoom()
          if (zoom && zoom > 12) {
            this.map.setZoom(12)
          }
        }, 100)
      }, 100)
    }
  }

  debugShowroomData () {
    if (!window.showroomLocations || window.showroomLocations.length === 0) {
      return
    }
    const possibleIdProps = ['id', 'name', 'showroomId', 'showroom_id', 'closest_city']
    const uniqueProp = possibleIdProps.find(prop => window.showroomLocations[0][prop] !== undefined)
    if (uniqueProp) {
      this.showroomIdProperty = uniqueProp
    } else {
      this.showroomIdProperty = 'closest_city'
    }
  }

  decodeHtmlEntities (text) {
    if (!text) return ''
    const textArea = document.createElement('textarea')
    textArea.innerHTML = text
    return textArea.value
  }

  getCountryFlag (regionOrCode) {
    // Accept either a region handle (e.g. "france") OR an ISO 3166-1 alpha-2
    // country code directly (e.g. "FR"). Showrooms managed by the toolkit
    // expose an explicit `country_code` field and should hit the second branch.
    const code = regionOrCode && /^[A-Za-z]{2}$/.test(regionOrCode)
      ? regionOrCode.toUpperCase()
      : this.getCountryCodeFromRegion(regionOrCode)
    if (!code) return ''
    return code
      .split('')
      .map(char => String.fromCodePoint(char.charCodeAt(0) + 127397))
      .join('')
  }

  showAllMarkers () {
    this.showroomMarkers.forEach(marker => {
      this.setMarkerMap(marker, this.map)
    })
  }

  resetMarkersToDefault () {
    this.showAllMarkers()
  }

  ensureShowroomDataIntegrity (showroom) {
    // Ensure all required fields are present and properly formatted
    // Handle both snake_case and camelCase properties
    return {
      id: showroom.id || showroom.closestCity || showroom.closest_city || showroom.town || showroom.companyName || showroom.company_name,
      closestCity: showroom.closestCity || showroom.closest_city || '',
      town: showroom.town || '',
      companyName: showroom.companyName || showroom.company_name || '',
      streetAddress: showroom.streetAddress || showroom.street_address || '',
      postcode: showroom.postcode || '',
      phone: showroom.phone || '',
      email: showroom.email || '',
      website: showroom.website || '',
      websitePretty: showroom.websitePretty || showroom.website_pretty || showroom.website || '',
      region: showroom.region || '',
      regionName: showroom.regionName || showroom.region_name || '',
      regionHandle: showroom.regionHandle || showroom.region_handle || '',
      // Country code is an explicit per-showroom override (managed in the
      // toolkit admin and synced via the `tb_showroom` metaobject). When set,
      // it should be preferred over the region-handle-derived mapping below.
      countryCode: (showroom.countryCode || showroom.country_code || '').toUpperCase(),
      pinLat: showroom.pinLat || showroom.pin_lat || '',
      pinLong: showroom.pinLong || showroom.pin_long || '',
      distance: showroom.distance || 0,
      slug: showroom.slug || showroom.booking_slug || '',
      booking_enabled: showroom.booking_enabled !== false && showroom.booking_enabled !== 'false',
      is_official: showroom.is_official === true || showroom.is_official === 'true',
    }
  }

  handleBookShowroomUrlParams () {
    const urlParams = new URLSearchParams(window.location.search)
    const bookShowroom = urlParams.get('book_showroom')
    const showroomName = urlParams.get('showroom')

    if (bookShowroom !== '1' || !showroomName) return

    const showroom = window.showroomLocations?.find(
      s => s.slug?.toLowerCase() === showroomName.toLowerCase() ||
        s.closest_city?.toLowerCase() === showroomName.toLowerCase(),
    )

    if (!showroom || !this.isBookingEnabled(showroom)) return

    // <reusable-modal> lives near the end of the document and is a separate
    // deferred module script, so it can still be un-upgraded (and thus not
    // yet listening for "open:modal") at the point showroom-booking.js runs
    // its connectedCallback on initial page load. Wait for it to be defined
    // (and therefore synchronously upgraded/connected) before dispatching,
    // otherwise the auto-open request silently fires into the void. Manual
    // "Book now" clicks aren't affected since by then the whole page,
    // including reusable-modal.js, has already loaded.
    customElements.whenDefined('reusable-modal').then(() => {
      this.openBookingModalForShowroom(showroom)
    })
  }
}
const SHOWROOM_BOOKING_MODELS = ['GO', 'Classic', 'Lite', 'Lite XL', 'Cargo']
// Upper bound on how many months forward the "find the first available date"
// search will look before giving up (prevents an unbounded fetch loop for a
// showroom with no availability configured at all).
const MAX_MONTHS_TO_SEARCH = 12

function toYmd (date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function parseYmd (ymd) {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function startOfMonth (date) {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function addMonths (date, n) {
  return new Date(date.getFullYear(), date.getMonth() + n, 1)
}

function isSameDay (a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function mondayIndex (date) {
  return (date.getDay() + 6) % 7
}

/**
 * Returns "today" as a calendar-only `Date` (midnight, no timezone offset
 * baked in) as seen from the given IANA timezone, not the visitor's browser
 * timezone. Used so the booking calendar's past-day styling and default
 * selected date reflect the showroom's own "today", which can differ from
 * the visitor's local date near midnight. Falls back to the browser's local
 * date if `timezone` is missing or invalid.
 */
function todayInTimezone (timezone) {
  if (!timezone) return startOfDay(new Date())
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date())
    const get = (type) => Number(parts.find((p) => p.type === type)?.value)
    return new Date(get('year'), get('month') - 1, get('day'))
  } catch {
    return startOfDay(new Date())
  }
}

function formatLong (date, locale) {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

function formatTime (iso, locale, timeZone) {
  const options = {
    hour: 'numeric',
    minute: '2-digit',
  }
  if (timeZone) {
    options.timeZone = timeZone
  }
  return new Intl.DateTimeFormat(locale, options).format(new Date(iso))
}

function formatTimezoneLabel (timeZone, locale) {
  if (!timeZone) return ''
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone,
      timeZoneName: 'long',
    }).formatToParts(new Date())
    const tzPart = parts.find((p) => p.type === 'timeZoneName')
    return tzPart?.value || timeZone
  } catch {
    return timeZone
  }
}

export class ShowroomBookingModal {
  constructor (options) {
    this.apiBase = (options.apiBase || '').replace(/\/$/, '')
    this.store = options.store || ''
    this.locale = options.locale || 'en'
    this.isEuStore = options.isEuStore === true
    this.strings = options.strings || {}
    this.icons = options.icons || {}
    this.showroom = null
    this.showroomTimezone = null
    this.viewMonth = startOfMonth(new Date())
    this.selectedDate = null
    this.selectedSlot = null
    this.availabilityByDate = new Map()
    this.loadedMonths = new Set()
    this.monthFetches = new Map()
    this.monthLoading = false
    this.slotMinutes = null
    this.submitting = false
    this.onBodyClick = this.onBodyClick.bind(this)
    this.onBodyChange = this.onBodyChange.bind(this)
    this.onBodySubmit = this.onBodySubmit.bind(this)
    this.onModalClose = this.onModalClose.bind(this)
  }

  get modal () {
    return document.querySelector('reusable-modal')
  }

  /**
   * Pushes a GTM/GA4 event to the dataLayer for the on-site booking modal,
   * matching the shape of the old Calendly embed's "calendly_event" /
   * "calendly_event_type" pushes so existing GTM triggers/tags built for
   * Calendly keep firing without changes on the GTM side (Krasi/Edward
   * request, Jul 2026). `calendlyEventType` mirrors Calendly's values:
   * "event_type_viewed", "date_and_time_selected", "event_scheduled".
   */
  trackEvent (calendlyEventType, extra = {}) {
    try {
      if (typeof window === 'undefined' || !window.dataLayer) return
      const s = this.showroom || {}
      window.dataLayer.push({
        event: 'calendly_event',
        calendly_event_type: calendlyEventType,
        showroom_slug: s.slug || '',
        showroom_name: this.getShowroomName(),
        showroom_region: s.region || s.regionHandle || s.region_handle || '',
        ...extra,
      })
    } catch (error) {
      console.warn('Error tracking showroom booking event:', calendlyEventType, error)
    }
  }

  get body () {
    return this.modal?.querySelector('.reusable-modal__body')
  }

  get title () {
    return this.modal?.querySelector('.reusable-modal__title')
  }

  t (key, fallback, replacements) {
    const raw = this.strings[key]
    let value = raw !== undefined && raw !== null && raw !== '' ? raw : fallback
    if (replacements) {
      Object.entries(replacements).forEach(([k, v]) => {
        const replacement = v ?? ''
        // Support legacy {{ key }} (double-brace) AND {key} (single-brace, used in
        // section setting defaults — Shopify treats {{ }} as a dynamic-source binding
        // in the editor and shows red errors).
        value = value.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, 'g'), replacement)
        value = value.replace(new RegExp(`\\{\\s*${k}\\s*\\}`, 'g'), replacement)
      })
    }
    return value
  }

  open (showroom) {
    if (!showroom?.slug || showroom.booking_enabled === false) return
    this.showroom = showroom
    this.showroomTimezone = showroom.timezone || null
    this.viewMonth = startOfMonth(new Date())
    this.selectedDate = null
    this.selectedSlot = null
    this.availabilityByDate = new Map()
    this.loadedMonths = new Set()
    this.monthFetches = new Map()
    this.monthLoading = false
    this.slotMinutes = null
    this.submitting = false

    window.dispatchEvent(new CustomEvent('open:modal', {
      detail: {
        width: 'min(880px, 100svw)',
        padding: 'none',
        modalClassAdd: ['md:w-full'],
        modalClassRemove: ['md:w-auto'],
        headerClassAdd: ['absolute', 'h-[60px]', 'top-0', 'left-0', 'right-0', 'z-20', 'pointer-events-none'],
        headerClassRemove: ['border-b', 'grid', 'mb-4', 'pb-2'],
        bodyClassAdd: ['relative', 'z-10', 'p-4', 'pt-6', 'md:p-6', 'md:pt-6'],
        closeClassAdd: ['absolute', 'top-3', 'right-3', 'z-20', 'pointer-events-auto'],
      },
    }))

    const title = this.title
    if (title) {
      title.textContent = ''
    }

    const body = this.body
    if (body) {
      body.innerHTML = this.shellHtml()
      this.attachListeners()
    }

    this.loadAvailability()
    this.trackEvent('event_type_viewed')
  }

  attachListeners () {
    const body = this.body
    if (!body) return
    body.addEventListener('click', this.onBodyClick)
    body.addEventListener('change', this.onBodyChange)
    body.addEventListener('submit', this.onBodySubmit)
    window.addEventListener('close:modal', this.onModalClose, { once: true })
    window.addEventListener('hide:page-obscure', this.onModalClose, { once: true })
  }

  detachListeners () {
    const body = this.body
    if (body) {
      body.removeEventListener('click', this.onBodyClick)
      body.removeEventListener('change', this.onBodyChange)
      body.removeEventListener('submit', this.onBodySubmit)
    }
  }

  onModalClose () {
    this.detachListeners()
  }

  onBodyClick (event) {
    const actionEl = event.target.closest('[data-action]')
    if (!actionEl) return
    event.stopPropagation()
    const action = actionEl.dataset.action
    if (action === 'prev-month') {
      this.goToMonth(addMonths(this.viewMonth, -1))
    } else if (action === 'next-month') {
      this.goToMonth(addMonths(this.viewMonth, 1))
    } else if (action === 'pick-date') {
      const date = actionEl.dataset.date
      if (!date) return
      this.selectedDate = date
      this.selectedSlot = null
      this.renderCalendar()
      this.scrollSlotsIntoView()
    } else if (action === 'pick-slot') {
      if (actionEl.hasAttribute('loading')) return
      this.selectedSlot = {
        start: actionEl.dataset.start,
        end: actionEl.dataset.end,
      }
      this.trackEvent('date_and_time_selected', {
        showroom_slot_start: this.selectedSlot.start || '',
        showroom_slot_end: this.selectedSlot.end || '',
      })
      actionEl.setAttribute('loading', '')
      this.body?.querySelectorAll('.showroom-booking-modal__slot').forEach((slot) => {
        if (slot !== actionEl) slot.setAttribute('disabled', '')
      })
      setTimeout(() => {
        this.renderForm()
        this.scrollBodyToTop()
      }, 300)
    } else if (action === 'back-to-calendar') {
      this.selectedSlot = null
      this.renderPane()
    } else if (action === 'retry-pick-slot') {
      this.refreshAvailabilityAfterConflict()
    } else if (action === 'close') {
      window.dispatchEvent(new CustomEvent('close:modal'))
    }
  }

  onBodyChange (_event) {
    // Reserved for future date/select inputs.
  }

  onBodySubmit (event) {
    const form = event.target.closest('form.showroom-booking-modal__form')
    if (!form) return
    event.preventDefault()
    event.stopPropagation()
    this.submitBooking(form)
  }

  getShowroomName () {
    const s = this.showroom || {}
    return s.closest_city || s.closestCity || s.town || s.company_name || s.companyName || ''
  }

  shellHtml () {
    return `
      <div class="showroom-booking-modal">
        <div class="showroom-booking-modal__layout grid grid-cols-1 md:grid-cols-[2fr_3fr] md:grid-rows-[auto_1fr] gap-5 md:gap-x-8 md:gap-y-6">
          <aside class="showroom-booking-modal__details-top pr-10 md:pr-0 md:col-start-1 md:row-start-1">
            ${this.detailsTopHtml()}
          </aside>
          <section class="showroom-booking-modal__pane min-h-[320px] md:col-start-2 md:row-start-1 md:row-span-2 md:border-l md:border-clay md:pl-8">
            ${this.loadingHtml()}
          </section>
          <aside class="showroom-booking-modal__details-bottom md:col-start-1 md:row-start-2 md:self-end md:pr-0">
            ${this.detailsBottomHtml()}
          </aside>
        </div>
      </div>
    `
  }

  formatDuration (minutes) {
    if (!Number.isFinite(minutes) || minutes <= 0) return ''
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    const hoursLabel = hours > 0 ? this.t('durationHoursFormat', '{count} hr', { count: String(hours) }) : ''
    const minutesLabel = mins > 0 ? this.t('durationMinutesFormat', '{count} min', { count: String(mins) }) : ''
    return [hoursLabel, minutesLabel].filter(Boolean).join(' ')
  }

  detailsTopHtml () {
    const s = this.showroom || {}
    const name = this.getShowroomName()
    const company = s.company_name || s.companyName || ''
    const title = this.t('subtitleTemplate', 'TentBox Showroom Appointment – {showroom}', { showroom: name })
    const computedDuration = this.slotMinutes ? this.formatDuration(this.slotMinutes) : ''
    const duration = computedDuration || this.t('durationLabel', '1 hr')
    const isOfficial = s.is_official === true || s.is_official === 'true'
    const partner = isOfficial ? '' : this.t('partnerDisclaimer', '')

    const addressParts = []
    if (s.street_address || s.streetAddress) addressParts.push(s.street_address || s.streetAddress)
    if (s.town) addressParts.push(s.town)
    if (s.postcode) addressParts.push(s.postcode)
    const address = addressParts.join(', ')

    return `
      <h3 class="showroom-booking-modal__title text-lg font-semibold text-black leading-tight">${escapeHtml(title)}</h3>
      ${company ? `<div class="showroom-booking-modal__subtitle text-base font-normal normal-case text-black mt-1 mb-3">${escapeHtml(company)}</div>` : '<div class="mb-3"></div>'}
      <ul class="showroom-booking-modal__meta flex flex-col gap-2 text-sm text-black">
        ${duration
? `
          <li class="showroom-booking-modal__meta-item flex items-start gap-2">
            <span class="showroom-booking-modal__meta-icon tb-icon w-4 h-4 mt-0.5 shrink-0 text-iron">${this.icons.clock || ''}</span>
            <span>${escapeHtml(duration)}</span>
          </li>`
: ''}
        ${address
? `
          <li class="showroom-booking-modal__meta-item flex items-start gap-2">
            <span class="showroom-booking-modal__meta-icon tb-icon w-4 h-4 mt-0.5 shrink-0 text-iron">${this.icons.pin || ''}</span>
            <span>${escapeHtml(address)}</span>
          </li>`
: ''}
      </ul>
      ${partner ? `<p class="showroom-booking-modal__partner mt-4 text-sm text-iron">${escapeHtml(partner)}</p>` : ''}
    `
  }

  detailsBottomHtml () {
    const s = this.showroom || {}
    const name = this.getShowroomName()
    const contactHeading = this.t('contactHeading', '', { showroom: name })
    const phone = s.phone || ''
    const email = s.email || ''
    const website = s.website || ''
    const websitePretty = s.website_pretty || s.websitePretty || website

    if (!phone && !email && !website) return ''

    return `
      <div class="showroom-booking-modal__contact pt-4 border-t border-clay text-sm">
        ${contactHeading ? `<div class="showroom-booking-modal__contact-heading font-semibold text-black mb-2">${escapeHtml(contactHeading)}</div>` : ''}
        <ul class="showroom-booking-modal__contact-list flex flex-col gap-1.5">
          ${phone
? `
            <li class="showroom-booking-modal__contact-item flex items-center gap-2 min-w-0">
              <span class="showroom-booking-modal__contact-icon tb-icon w-4 h-4 shrink-0 text-iron">${this.icons.phone || ''}</span>
              <a href="tel:${escapeAttr(phone)}" class="showroom-booking-modal__contact-link min-w-0 flex-1 truncate underline underline-offset-2 hover:no-underline" title="${escapeAttr(phone)}">${escapeHtml(phone)}</a>
            </li>`
: ''}
          ${email
? `
            <li class="showroom-booking-modal__contact-item flex items-center gap-2 min-w-0">
              <span class="showroom-booking-modal__contact-icon tb-icon w-4 h-4 shrink-0 text-iron">${this.icons.email || ''}</span>
              <a href="mailto:${escapeAttr(email)}" class="showroom-booking-modal__contact-link min-w-0 flex-1 truncate underline underline-offset-2 hover:no-underline" title="${escapeAttr(email)}">${escapeHtml(email)}</a>
            </li>`
: ''}
          ${website
? `
            <li class="showroom-booking-modal__contact-item flex items-center gap-2 min-w-0">
              <span class="showroom-booking-modal__contact-icon tb-icon w-4 h-4 shrink-0 text-iron">${this.icons.globe || ''}</span>
              <a href="${escapeAttr(website)}" target="_blank" rel="noopener noreferrer" class="showroom-booking-modal__contact-link min-w-0 flex-1 truncate underline underline-offset-2 hover:no-underline" title="${escapeAttr(websitePretty)}">${escapeHtml(websitePretty)}</a>
            </li>`
: ''}
        </ul>
      </div>
    `
  }

  loadingHtml () {
    const label = this.t('loading', 'Loading availability…')
    return `
      <div class="showroom-booking-modal__state flex h-full min-h-[320px] flex-col items-center justify-center text-center text-iron py-12 gap-3">
        <span class="tb-icon h-8 w-8 animate-spin text-orange">${this.icons.spin || ''}</span>
        <p class="text-sm">${escapeHtml(label)}</p>
      </div>
    `
  }

  errorHtml (message) {
    return `
      <div class="showroom-booking-modal__state flex h-full min-h-[320px] flex-col items-center justify-center text-center py-12 gap-3">
        <span class="tb-icon h-8 w-8 text-danger">${this.icons.warning || ''}</span>
        <p class="text-sm text-danger">${escapeHtml(message)}</p>
      </div>
    `
  }

  emptyHtml () {
    const message = this.t('noAvailability', 'No appointments available right now.')
    return `
      <div class="showroom-booking-modal__state flex h-full min-h-[320px] flex-col items-center justify-center text-center py-12 gap-3">
        <span class="tb-icon h-8 w-8 text-iron">${this.icons.calendar || ''}</span>
        <p class="text-sm text-iron">${escapeHtml(message)}</p>
      </div>
    `
  }

  monthKey (date) {
    return `${date.getFullYear()}-${date.getMonth()}`
  }

  /**
   * Fetches and caches availability for a single calendar month. Safe to
   * call repeatedly — already-loaded or in-flight months are deduped so
   * navigating back and forth never re-fetches. Merges results into the
   * shared `availabilityByDate` map rather than replacing it, so previously
   * loaded months stay visible.
   */
  async ensureMonthLoaded (monthDate) {
    const key = this.monthKey(monthDate)
    if (this.loadedMonths.has(key)) return
    if (this.monthFetches.has(key)) return this.monthFetches.get(key)
    if (!this.apiBase || !this.store || !this.showroom?.slug) return

    const from = startOfMonth(monthDate)
    const to = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0)
    const url = `${this.apiBase}/api/showrooms/availability?store=${encodeURIComponent(this.store)}&showroom=${encodeURIComponent(this.showroom.slug)}&from=${toYmd(from)}&to=${toYmd(to)}`

    const fetchPromise = (async () => {
      const res = await fetch(url, { credentials: 'omit' })
      const contentType = res.headers.get('content-type') || ''
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`)
      }
      if (!contentType.includes('application/json')) {
        throw new Error('Invalid API response')
      }
      const data = await res.json()
      if (data.error) {
        throw new Error(data.error)
      }

      if (data.timezone) {
        this.showroomTimezone = data.timezone
      } else if (this.showroom?.timezone) {
        this.showroomTimezone = this.showroom.timezone
      }

      const days = (data.days || []).filter((d) => Array.isArray(d.slots) && d.slots.length > 0)
      days.forEach((d) => this.availabilityByDate.set(d.date, d.slots))

      if (this.slotMinutes === null || this.slotMinutes === undefined) {
        const firstSlot = days[0]?.slots?.[0]
        if (firstSlot?.start && firstSlot?.end) {
          const diffMs = new Date(firstSlot.end).getTime() - new Date(firstSlot.start).getTime()
          const diffMinutes = Math.round(diffMs / 60000)
          if (Number.isFinite(diffMinutes) && diffMinutes > 0) {
            this.slotMinutes = diffMinutes
          }
        }
      }

      this.loadedMonths.add(key)
    })().finally(() => {
      this.monthFetches.delete(key)
    })

    this.monthFetches.set(key, fetchPromise)
    return fetchPromise
  }

  /** Finds the first date (today or later) with availability, in-order,
   * loading one month at a time so we never fetch further than needed. */
  async findFirstAvailableDate () {
    let month = startOfMonth(new Date())
    for (let i = 0; i < MAX_MONTHS_TO_SEARCH; i += 1) {
      try {
        await this.ensureMonthLoaded(month)
      } catch (err) {
        console.error('Showroom availability load failed:', err)
        throw err
      }
      const today = todayInTimezone(this.showroomTimezone)
      const match = [...this.availabilityByDate.keys()]
        .filter((ymd) => parseYmd(ymd) >= today && parseYmd(ymd) < addMonths(month, 1))
        .sort()[0]
      if (match) return { date: match, month }
      month = addMonths(month, 1)
    }
    return null
  }

  async loadAvailability () {
    if (!this.apiBase || !this.store || !this.showroom?.slug) {
      this.renderPaneHtml(this.errorHtml(this.t('loadError', 'Could not load availability. Please try again.')))
      return
    }

    try {
      const first = await this.findFirstAvailableDate()
      if (first) {
        this.viewMonth = first.month
        this.selectedDate = first.date
      }

      this.renderDetailsTop()
      this.renderPane()
    } catch (err) {
      console.error('Showroom availability load failed:', err)
      this.renderPaneHtml(this.errorHtml(this.t('loadError', 'Could not load availability. Please try again.')))
    }
  }

  /**
   * Recovers from a booking rejection where the selected slot is no longer
   * available (taken by someone else, capacity/day-limit reached, or moved
   * into the buffer/notice window while the visitor filled in the form).
   * Drops the stale cached day (a now-fully-booked day is simply absent from
   * a fresh API response, so a stale cache entry would otherwise linger with
   * slots that no longer exist) and the slot's month, then re-fetches before
   * returning to the calendar so the visitor sees genuinely current slots.
   */
  async refreshAvailabilityAfterConflict () {
    const conflictDate = this.selectedSlot?.start?.slice(0, 10) || this.selectedDate
    this.selectedSlot = null
    if (conflictDate) this.availabilityByDate.delete(conflictDate)

    const month = conflictDate ? new Date(conflictDate) : this.viewMonth
    const key = this.monthKey(month)
    this.loadedMonths.delete(key)

    this.monthLoading = true
    this.renderPane()
    try {
      await this.ensureMonthLoaded(month)
    } catch (err) {
      console.error('Showroom availability refresh failed:', err)
    } finally {
      this.monthLoading = false
      this.renderPane()
      this.scrollBodyToTop()
    }
  }

  /** Navigates the calendar to a new month, fetching it first if needed. */
  async goToMonth (monthDate) {
    this.viewMonth = monthDate
    const key = this.monthKey(monthDate)
    if (this.loadedMonths.has(key)) {
      this.renderCalendar()
      return
    }
    this.monthLoading = true
    this.renderCalendar()
    try {
      await this.ensureMonthLoaded(monthDate)
    } catch (err) {
      console.error('Showroom availability load failed:', err)
    } finally {
      this.monthLoading = false
      this.renderCalendar()
    }
  }

  renderPane () {
    if (this.availabilityByDate.size === 0 && !this.monthLoading) {
      this.renderPaneHtml(this.emptyHtml())
      return
    }
    this.renderCalendar()
  }

  renderPaneHtml (html) {
    const pane = this.body?.querySelector('.showroom-booking-modal__pane')
    if (!pane) return
    pane.innerHTML = html
  }

  get bodyWrapper () {
    return this.modal?.querySelector('.reusable-modal__body-wrapper')
  }

  /** On mobile, the calendar and time slots stack vertically and the slots
   * can sit below the fold — scroll them into view once a date is picked so
   * it's obvious more content (the available times) appeared below. */
  scrollSlotsIntoView () {
    requestAnimationFrame(() => {
      const slots = this.body?.querySelector('.showroom-booking-modal__slots')
      slots?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    })
  }

  /** Scrolls the modal back to the top when the form appears, since the
   * visitor is typically scrolled down to the time slots at that point. */
  scrollBodyToTop () {
    requestAnimationFrame(() => {
      this.bodyWrapper?.scrollTo({ top: 0, behavior: 'smooth' })
    })
  }

  renderDetailsTop () {
    const aside = this.body?.querySelector('.showroom-booking-modal__details-top')
    if (!aside) return
    aside.innerHTML = this.detailsTopHtml()
  }

  renderCalendar () {
    const pane = this.body?.querySelector('.showroom-booking-modal__pane')
    if (!pane) return
    pane.innerHTML = this.calendarHtml()
  }

  calendarHtml () {
    const today = todayInTimezone(this.showroomTimezone)
    const monthStart = this.viewMonth
    const monthLabel = new Intl.DateTimeFormat(this.locale, { month: 'long', year: 'numeric' }).format(monthStart)

    const firstWeekdayOffset = mondayIndex(monthStart)
    const daysInMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate()

    const weekdays = []
    const weekdayFmt = new Intl.DateTimeFormat(this.locale, { weekday: 'short' })
    for (let i = 0; i < 7; i += 1) {
      // Use any known Monday so weekday labels are correct regardless of locale month.
      const d = new Date(2024, 0, 1 + i) // 2024-01-01 was a Monday
      weekdays.push(weekdayFmt.format(d))
    }

    const totalCells = Math.ceil((firstWeekdayOffset + daysInMonth) / 7) * 7
    const cells = []
    for (let i = 0; i < totalCells; i += 1) {
      const dayNum = i - firstWeekdayOffset + 1
      if (dayNum < 1 || dayNum > daysInMonth) {
        cells.push('<span class="showroom-booking-modal__day showroom-booking-modal__day--empty"></span>')
        continue
      }
      const date = new Date(monthStart.getFullYear(), monthStart.getMonth(), dayNum)
      const ymd = toYmd(date)
      const isPast = date < today
      const hasSlots = this.availabilityByDate.has(ymd)
      const isSelected = ymd === this.selectedDate
      const isToday = isSameDay(date, today)

      const classes = ['showroom-booking-modal__day']
      if (isPast) classes.push('showroom-booking-modal__day--past')
      if (hasSlots && !isPast) classes.push('showroom-booking-modal__day--available')
      if (isSelected) classes.push('showroom-booking-modal__day--selected')
      if (isToday) classes.push('showroom-booking-modal__day--today')

      if (hasSlots && !isPast) {
        cells.push(`<button type="button" class="${classes.join(' ')}" data-action="pick-date" data-date="${ymd}" aria-pressed="${isSelected}"><span>${dayNum}</span></button>`)
      } else {
        cells.push(`<span class="${classes.join(' ')}" aria-disabled="true"><span>${dayNum}</span></span>`)
      }
    }

    const prevDisabled = this.monthLoading || startOfMonth(monthStart).getTime() <= startOfMonth(today).getTime()
    const nextDisabled = this.monthLoading
    const isCurrentMonthLoading = this.monthLoading && !this.loadedMonths.has(this.monthKey(monthStart))
    const slotsHtml = isCurrentMonthLoading
      ? `
      <div class="showroom-booking-modal__slots-loading text-sm text-iron mt-6 pt-4 border-t border-clay flex items-center gap-2">
        <span class="tb-icon w-4 h-4 animate-spin text-iron">${this.icons.spin || ''}</span>
        ${escapeHtml(this.t('loading', 'Loading availability…'))}
      </div>
    `
      : this.selectedDate
        ? this.slotsHtml(this.selectedDate)
        : `
      <div class="showroom-booking-modal__slots-empty text-sm text-iron mt-6 pt-4 border-t border-clay">
        ${escapeHtml(this.t('selectDatePrompt', 'Select a date to see available times.'))}
      </div>
    `

    return `
      <div class="showroom-booking-modal__calendar">
        <div class="showroom-booking-modal__calendar-header flex items-center justify-between mb-3">
          <h3 class="text-base font-semibold text-black">${escapeHtml(this.t('calendarHeading', 'Select a date & time'))}</h3>
        </div>
        <div class="showroom-booking-modal__calendar-nav flex items-center justify-between mb-3">
          <button
            type="button"
            class="showroom-booking-modal__nav-btn h-9 w-9 inline-flex items-center justify-center rounded-full border border-clay text-black hover:border-black disabled:opacity-30 disabled:hover:border-clay disabled:cursor-not-allowed"
            data-action="prev-month"
            aria-label="${escapeAttr(this.t('prevMonth', 'Previous month'))}"
            ${prevDisabled ? 'disabled' : ''}
          >
            <span class="tb-icon w-4 h-4">${this.icons.chevronLeft || ''}</span>
          </button>
          <div class="showroom-booking-modal__calendar-month text-sm font-semibold text-black">${escapeHtml(monthLabel)}</div>
          <button
            type="button"
            class="showroom-booking-modal__nav-btn h-9 w-9 inline-flex items-center justify-center rounded-full border border-clay text-black hover:border-black disabled:opacity-30 disabled:hover:border-clay disabled:cursor-not-allowed"
            data-action="next-month"
            aria-label="${escapeAttr(this.t('nextMonth', 'Next month'))}"
            ${nextDisabled ? 'disabled' : ''}
          >
            <span class="tb-icon w-4 h-4">${this.icons.chevronRight || ''}</span>
          </button>
        </div>
        <div class="showroom-booking-modal__calendar-weekdays grid grid-cols-7 mb-1">
          ${weekdays.map((w) => `<span class="text-[11px] uppercase tracking-wide text-iron text-center py-1">${escapeHtml(w)}</span>`).join('')}
        </div>
        <div class="showroom-booking-modal__calendar-grid grid grid-cols-7 gap-1">
          ${cells.join('')}
        </div>
        ${slotsHtml}
      </div>
    `
  }

  slotsHtml (ymd) {
    const slots = this.availabilityByDate.get(ymd) || []
    const date = parseYmd(ymd)
    const heading = this.t('slotsHeading', 'Available times — {date}', {
      date: formatLong(date, this.locale),
    })
    // Times are always generated/returned in the showroom's local timezone,
    // not the visitor's — call that out above the slot grid on EU stores,
    // where showrooms span multiple timezones.
    const tz = this.isEuStore && this.showroomTimezone
      ? formatTimezoneLabel(this.showroomTimezone, this.locale)
      : ''
    const timezoneNoteHtml = tz
      ? `<p class="showroom-booking-modal__timezone-note text-xs text-iron mb-3">${escapeHtml(this.t('timezoneNote', 'Times shown in {tz}', { tz }))}</p>`
      : ''
    if (slots.length === 0) {
      return `
        <div class="showroom-booking-modal__slots mt-6 pt-4 border-t border-clay">
          <h4 class="text-sm font-semibold text-black mb-3">${escapeHtml(heading)}</h4>
          ${timezoneNoteHtml}
          <p class="text-sm text-iron">${escapeHtml(this.t('noSlotsForDay', 'No appointments available for this date.'))}</p>
        </div>
      `
    }
    const buttons = slots.map((slot) => {
      const label = formatTime(slot.start, this.locale, this.showroomTimezone)
      return `<button type="button" class="showroom-booking-modal__slot relative h-11 px-3 inline-flex items-center justify-center rounded border border-clay text-sm font-medium text-black hover:border-black hover:bg-oat transition-colors" data-action="pick-slot" data-start="${escapeAttr(slot.start)}" data-end="${escapeAttr(slot.end)}">
        <span class="showroom-booking-modal__slot-label">${escapeHtml(label)}</span>
        <span class="showroom-booking-modal__slot-spinner loading-overlay__spinner absolute inset-0 hidden items-center justify-center bg-white/60 rounded" aria-hidden="true">
          <span class="tb-icon h-5 w-5 animate-spin text-black">${this.icons.spin || ''}</span>
        </span>
      </button>`
    }).join('')
    return `
      <div class="showroom-booking-modal__slots mt-6 pt-4 border-t border-clay">
        <h4 class="text-sm font-semibold text-black mb-3">${escapeHtml(heading)}</h4>
        ${timezoneNoteHtml}
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">${buttons}</div>
      </div>
    `
  }

  renderForm () {
    this.renderPaneHtml(this.formHtml())
    const firstInput = this.body?.querySelector('.showroom-booking-modal__form input[name="name"]')
    if (firstInput) firstInput.focus({ preventScroll: true })
  }

  formHtml () {
    const slot = this.selectedSlot
    const date = this.selectedDate ? parseYmd(this.selectedDate) : null
    const summary = date && slot ? `${formatLong(date, this.locale)} · ${formatTime(slot.start, this.locale, this.showroomTimezone)} – ${formatTime(slot.end, this.locale, this.showroomTimezone)}` : ''
    const tz = this.showroomTimezone
      ? formatTimezoneLabel(this.showroomTimezone, this.locale)
      : ''

    return `
      <div class="showroom-booking-modal__form-view">
        <button type="button" class="showroom-booking-modal__back inline-flex items-center gap-1 text-sm text-iron hover:text-black mb-3" data-action="back-to-calendar">
          <span class="tb-icon w-4 h-4">${this.icons.chevronLeft || ''}</span>
          <span>${escapeHtml(this.t('backLabel', 'Back'))}</span>
        </button>
        <div class="showroom-booking-modal__summary rounded-lg border border-clay bg-oat p-3 mb-4">
          <div class="text-sm font-semibold text-black">${escapeHtml(summary)}</div>
          ${tz ? `<div class="text-xs text-iron mt-0.5">${escapeHtml(this.t('timezoneNote', 'Times shown in {tz}', { tz }))}</div>` : ''}
        </div>
        <form class="showroom-booking-modal__form flex flex-col gap-3" novalidate>
          ${this.fieldHtml({ name: 'name', type: 'text', autocomplete: 'name', required: true, label: this.t('nameLabel', 'Your name') })}
          ${this.fieldHtml({ name: 'email', type: 'email', autocomplete: 'email', required: true, label: this.t('emailLabel', 'Email address') })}
          ${this.fieldHtml({ name: 'phone', type: 'tel', autocomplete: 'tel', required: false, label: this.t('phoneLabel', 'Phone (optional)') })}
          ${this.optionalQuestionsHtml()}
          <p class="showroom-booking-modal__error text-sm text-danger hidden" role="alert"></p>
          <button type="submit" class="showroom-booking-modal__submit button button-orange h-11 w-full mt-1">
            <span class="showroom-booking-modal__submit-label">${escapeHtml(this.t('confirmLabel', 'Confirm booking'))}</span>
            <span class="showroom-booking-modal__submit-spinner tb-icon h-5 w-5 ml-2 animate-spin hidden">${this.icons.spin || ''}</span>
          </button>
        </form>
      </div>
    `
  }

  fieldHtml ({ name, type, autocomplete, required, label }) {
    const id = `showroom-booking-modal-${name}`
    return `
      <div class="showroom-booking-modal__field">
        <div class="showroom-booking-modal__input-container relative">
          <input
            type="${type}"
            name="${name}"
            id="${id}"
            autocomplete="${autocomplete}"
            placeholder="${escapeAttr(label)}"
            class="showroom-booking-modal__input h-[46px] w-full rounded border border-clay px-3.5 focus-visible:outline-hidden text-base text-black focus:ring-0 focus:border-black hover:border-black"
            ${required ? 'required' : ''}
          />
          <label for="${id}" class="showroom-booking-modal__floating-label">${escapeHtml(label)}</label>
        </div>
      </div>
    `
  }

  optionalQuestionsHtml () {
    const modelButtons = SHOWROOM_BOOKING_MODELS.map((model) => {
      const id = `showroom-booking-modal-model-${model.replace(/\s+/g, '-').toLowerCase()}`
      return `
        <label for="${id}" class="showroom-booking-modal__model-option inline-flex items-center gap-1.5 text-sm text-black">
          <input type="checkbox" id="${id}" name="models" value="${escapeAttr(model)}" class="showroom-booking-modal__checkbox" />
          <span>${escapeHtml(model)}</span>
        </label>
      `
    }).join('')

    return `
      <div class="showroom-booking-modal__optional-questions flex flex-col gap-3 pt-2 mt-1 border-t border-clay">
        <div class="showroom-booking-modal__field">
          <span class="showroom-booking-modal__field-label text-sm font-medium text-black block mb-1.5">${escapeHtml(this.t('modelsHeading', 'Which model(s) are you interested in? (optional)'))}</span>
          <div class="showroom-booking-modal__models flex flex-wrap gap-3">${modelButtons}</div>
        </div>
        ${this.yesNoFieldHtml('roof_bars', this.t('roofBarsLabel', 'Do you need roof bars? (optional)'))}
        ${this.yesNoFieldHtml('installation_interest', this.t('installationInterestLabel', 'Interested in installation? (optional)'))}
        <div class="showroom-booking-modal__field">
          <label for="showroom-booking-modal-notes" class="showroom-booking-modal__field-label text-sm font-medium text-black block mb-1.5">${escapeHtml(this.t('notesLabel', 'Anything else we should know? (optional)'))}</label>
          <textarea
            name="additional_notes"
            id="showroom-booking-modal-notes"
            rows="3"
            class="showroom-booking-modal__textarea w-full rounded border border-clay px-3.5 py-2.5 focus-visible:outline-hidden text-base text-black focus:ring-0 focus:border-black hover:border-black"
          ></textarea>
        </div>
      </div>
    `
  }

  yesNoFieldHtml (name, label) {
    const yesId = `showroom-booking-modal-${name}-yes`
    const noId = `showroom-booking-modal-${name}-no`
    const yesLabel = this.t('yesLabel', 'Yes')
    const noLabel = this.t('noLabel', 'No')
    return `
      <div class="showroom-booking-modal__field">
        <span class="showroom-booking-modal__field-label text-sm font-medium text-black block mb-1.5">${escapeHtml(label)}</span>
        <div class="showroom-booking-modal__radio-group flex gap-4">
          <label for="${yesId}" class="inline-flex items-center gap-1.5 text-sm text-black">
            <input type="radio" id="${yesId}" name="${name}" value="yes" class="showroom-booking-modal__radio" />
            <span>${escapeHtml(yesLabel)}</span>
          </label>
          <label for="${noId}" class="inline-flex items-center gap-1.5 text-sm text-black">
            <input type="radio" id="${noId}" name="${name}" value="no" class="showroom-booking-modal__radio" />
            <span>${escapeHtml(noLabel)}</span>
          </label>
        </div>
      </div>
    `
  }

  async submitBooking (form) {
    if (this.submitting) return
    const errorEl = form.querySelector('.showroom-booking-modal__error')
    const submitBtn = form.querySelector('.showroom-booking-modal__submit')
    const submitLabel = form.querySelector('.showroom-booking-modal__submit-label')
    const submitSpinner = form.querySelector('.showroom-booking-modal__submit-spinner')

    const setError = (message) => {
      if (!errorEl) return
      if (message) {
        errorEl.textContent = message
        errorEl.classList.remove('hidden')
      } else {
        errorEl.textContent = ''
        errorEl.classList.add('hidden')
      }
    }

    // Same as setError, but appends an inline "choose another time" link
    // (data-action="retry-pick-slot", handled in onBodyClick) for failures
    // where the fix is simply picking a different slot.
    const setRetryableError = (message, actionLabel) => {
      if (!errorEl) return
      errorEl.innerHTML = `${escapeHtml(message)} <button type="button" class="showroom-booking-modal__error-action underline font-medium text-black" data-action="retry-pick-slot">${escapeHtml(actionLabel)}</button>`
      errorEl.classList.remove('hidden')
    }

    if (!this.selectedSlot) {
      setError(this.t('pickSlot', 'Please choose a time slot.'))
      return
    }

    const name = form.name.value.trim()
    const email = form.email.value.trim()
    const phone = form.phone.value.trim()
    const models = Array.from(form.querySelectorAll('input[name="models"]:checked')).map((el) => el.value)
    const roofBars = form.querySelector('input[name="roof_bars"]:checked')?.value ?? null
    const installationInterest = form.querySelector('input[name="installation_interest"]:checked')?.value ?? null
    const additionalNotes = form.additional_notes?.value.trim() || null

    if (!name) {
      setError(this.t('nameRequired', 'Please enter your name.'))
      form.name.focus()
      return
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(this.t('emailInvalid', 'Please enter a valid email address.'))
      form.email.focus()
      return
    }

    setError(null)
    this.submitting = true
    if (submitBtn) submitBtn.disabled = true
    if (submitLabel) submitLabel.classList.add('opacity-60')
    if (submitSpinner) submitSpinner.classList.remove('hidden')

    const payload = {
      store: this.store,
      showroom: this.showroom.slug,
      slot_start: this.selectedSlot.start,
      slot_end: this.selectedSlot.end,
      name,
      email,
      phone: phone || null,
      locale: this.locale,
      models,
      roof_bars: yesNoToBoolean(roofBars),
      installation_interest: yesNoToBoolean(installationInterest),
      additional_notes: additionalNotes,
    }

    const vehicleChecker = readVehicleCheckerDetails()
    if (vehicleChecker) {
      payload.vehicle_checker = vehicleChecker
    }

    try {
      const res = await fetch(`${this.apiBase}/api/showrooms/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify(payload),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        // The toolkit API returns both a human-readable `error` string (which
        // can change independently) and a stable `code` for branching logic.
        // Older toolkit deployments may omit `code` — fall back to matching
        // known legacy `error` strings so this degrades gracefully.
        const err = new Error(data.error || 'BOOKING_FAILED')
        err.code = data.code || legacyErrorCode(data.error)
        throw err
      }
      this.trackEvent('event_scheduled', {
        showroom_booking_code: data.booking_code || '',
        // Mirrors Calendly's stringified "calendly_payload" field so any
        // downstream GTM variable expecting a JSON string keeps working.
        // Content is entirely ours (different system, not Calendly API
        // URIs) — deliberately excludes name/email: GA4/GTM dataLayer
        // pushes must not carry PII (Google policy), so only booking
        // identifiers go here.
        calendly_payload: JSON.stringify({
          booking: {
            code: data.booking_code || '',
            store: this.store,
            showroom_slug: this.showroom.slug,
            slot_start: this.selectedSlot?.start || '',
            slot_end: this.selectedSlot?.end || '',
          },
        }),
      })
      this.renderSuccess(data.booking_code)
    } catch (err) {
      const mapped = this.mapBookingError(err)
      if (mapped.retryable) {
        setRetryableError(mapped.text, this.t('chooseAnotherTime', 'Choose another time'))
      } else {
        setError(mapped.text)
      }
      if (submitBtn) submitBtn.disabled = false
      if (submitLabel) submitLabel.classList.remove('opacity-60')
      if (submitSpinner) submitSpinner.classList.add('hidden')
      this.submitting = false
    }
  }

  /**
   * Maps a booking failure to display copy. `retryable: true` means the slot
   * itself is the problem (already taken, just filled up while the visitor
   * was filling in the form, or the buffer/notice window moved past it) —
   * for those we show a "choose another time" action that refreshes
   * availability and returns to the calendar, rather than a dead-end message.
   */
  mapBookingError (err) {
    const code = err && err.code
    if (code === 'SLOT_UNAVAILABLE' || code === 'SLOT_FULL') {
      return {
        text: this.t('slotTakenError', 'That time slot was just taken. Please pick another.'),
        retryable: true,
      }
    }
    if (code === 'DAY_LIMIT_REACHED') {
      return {
        text: this.t('dayFullError', 'This showroom is now fully booked for that day. Please pick another date.'),
        retryable: true,
      }
    }
    if (code === 'RATE_LIMITED') {
      return {
        text: this.t('rateLimitedError', 'Too many requests. Please wait a moment and try again.'),
        retryable: false,
      }
    }
    if (code === 'VALIDATION_FAILED') {
      return {
        text: this.t('validationError', 'Please check your details and try again.'),
        retryable: false,
      }
    }
    return {
      text: this.t('bookError', 'Booking failed. Please try again.'),
      retryable: false,
    }
  }

  renderSuccess (bookingCode) {
    const pane = this.body?.querySelector('.showroom-booking-modal__pane')
    if (!pane) return
    const heading = this.t('successHeading', 'Booking confirmed')
    const body = this.t('successBody', 'We\u2019ve sent confirmation to your inbox. Your reference is {code}.', { code: bookingCode || '' })
    const closeLabel = this.t('closeLabel', 'Close')
    pane.innerHTML = `
      <div class="showroom-booking-modal__success flex flex-col items-center text-center py-10 px-2 gap-3">
        <span class="tb-icon h-12 w-12 text-success">${this.icons.checkCircle || ''}</span>
        <h3 class="text-lg font-semibold text-black">${escapeHtml(heading)}</h3>
        <p class="text-sm text-iron max-w-sm">${escapeHtml(body)}</p>
        <button type="button" class="button button-outline h-11 mt-2" data-action="close">${escapeHtml(closeLabel)}</button>
      </div>
    `
  }
}

function startOfDay (date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function escapeHtml (value) {
  if (value === null || value === undefined) return ''
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function escapeAttr (value) {
  return escapeHtml(value)
}

function yesNoToBoolean (value) {
  if (value === 'yes') return true
  if (value === 'no') return false
  return null
}

/** Fallback mapping from the toolkit's human-readable `error` string to a
 * stable code, used only if an older toolkit deployment omits `code`. */
function legacyErrorCode (message) {
  switch (message) {
    case 'Slot is full':
      return 'SLOT_FULL'
    case 'Slot not available':
      return 'SLOT_UNAVAILABLE'
    case 'This showroom is fully booked for that day':
      return 'DAY_LIMIT_REACHED'
    case 'Too many requests. Please try again later.':
      return 'RATE_LIMITED'
    default:
      return null
  }
}

/**
 * Reads the vehicle-checker tool's saved selection/compatibility out of
 * localStorage (set by assets/vehicle-checker.js) and flattens it into the
 * shape the toolkit booking API expects. Returns null if the visitor hasn't
 * used the vehicle checker (or the data is malformed/stale).
 */
function readVehicleCheckerDetails () {
  let selection = null
  let compatibility = null
  try {
    const rawSelection = localStorage.getItem('vehicleSelection')
    if (rawSelection) selection = JSON.parse(rawSelection)
  } catch {
    selection = null
  }
  try {
    const rawCompatibility = localStorage.getItem('vehicleCompatibility')
    if (rawCompatibility) compatibility = JSON.parse(rawCompatibility)
  } catch {
    compatibility = null
  }

  if (!selection && !compatibility) return null

  return {
    brand: selection?.brand?.name ?? null,
    series: selection?.series?.name ?? null,
    model: selection?.model?.name ?? null,
    generation: selection?.generation?.label ?? null,
    roofLoadLimit: compatibility?.roofLoadLimit ?? selection?.generation?.roofLoadLimit ?? null,
    compatibleProducts: Array.isArray(compatibility?.compatibleProducts) ? compatibility.compatibleProducts : null,
  }
}

if (!customElements.get('showroom-booking')) {
  customElements.define('showroom-booking', ShowroomBooking)
}
